import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Chat page — turn-based transcript.
//
// State model: instead of "messages + side activity", every user prompt opens
// a NEW Turn that collects everything the agent does in response (tools,
// file changes, sub-agent spawns, commits, end-of-turn summary). The page
// renders a vertical stack of TurnCards. Each turn is self-contained — you
// can scroll back to any turn and see exactly what happened.
//
// Event routing:
//   user_message              → open a new currentTurn
//   assistant_text(chunk)     → append to currentTurn.assistantText
//   assistant_text(done=true) → mark currentTurn.isStreaming = false
//   tool_start / tool_done    → push / patch currentTurn.tools
//   file_changed              → push to currentTurn.fileChanges
//   agent_spawn / agent_status_update → update currentTurn.agents
//   commit_made / commit_skipped      → push to currentTurn.commits
//   push_done                 → refresh git info (no per-turn card)
//   turn_summary              → freeze currentTurn (push to turns[]),
//                                update session totals
//   error                     → set currentTurn.error
//   todo_update               → updates the sticky PlanPanel (turn-independent)
//
// Reconstructing on mount: fetch /messages + /events, walk the event log,
// fold into turns[]. This gives full restore-on-reload behaviour.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useOutletContext, useLocation } from "react-router-dom";
import { sessionApi, sessionsApi, deployedAppsApi } from "@/lib/api";
import { openEventStream } from "@/lib/ws";
import { useTheme } from "@/lib/theme";
import { useSessions } from "@/lib/sessionContext";
// Chat-visible auto-compact breadcrumb. One per `context_compacted` event
// (server-side `maybe_compact` in memory/checkpointer.py fires this when
// the message list crosses the auto-compact threshold). Renders as a
// collapsible system message in the transcript so the user can SEE that
// older turns got summarised and what the agent now remembers about them.
// The `ContextCompactedNote` type used by the compact-card state and
// the live WS handler lives in `@/lib/types` so the same shape can be
// embedded in `Turn.compactNotes` (for chronological placement inside
// the turn during which the compact fired). The local handler here
// imports it under the same name.
import PlanPanel from "@/components/PlanPanel";
import LLMTracePanel from "@/components/LLMTracePanel";
import TurnCard, { ActiveTurnCard } from "@/components/TurnCard";
import RunningTotals from "@/components/RunningTotals";
import { formatDuration, formatTokens } from "@/lib/format";
import { useAppSettings } from "@/lib/appSettings";
import { SunIcon, MoonIcon } from "@/components/icons";
// Single source of truth for slash commands. Used by both the inline
// Max height of the chat composer textarea, in pixels. 24px line-height ×
// 6 lines = 144px. Above this, the textarea shows an internal scrollbar
// instead of growing further. Picked to match Claude.ai's composer cap.
const MAX_COMPOSER_HEIGHT = 144;
// Slash-command list. Surfaced via two UI surfaces: the `/`-trigger
// autocomplete picker (live, as the user types) and the /help overlay (full
// reference). Keep `cmd` lowercase and starting with "/".
const SLASH_COMMANDS = [
    { cmd: "/help", desc: "Show all commands and keyboard shortcuts" },
    { cmd: "/clear", desc: "Clear the current view (server history is kept)" },
    { cmd: "/stop", desc: "Cancel the in-flight turn (same as the Stop button)" },
    { cmd: "/cancel", desc: "Alias of /stop" },
    { cmd: "/history", desc: "Show the last 50 prompts you've sent in this session" },
    { cmd: "/compact", desc: "Compact the agent's context now (summarise old turns, keep recent)" },
    { cmd: "/debug", desc: "Toggle the raw WebSocket event panel (for troubleshooting)" },
];
const EMPTY_TOTALS = {
    turns: 0, tools: 0,
    inputTokens: 0, outputTokens: 0,
    cacheReadTokens: 0, cacheWriteTokens: 0,
    costUsd: 0, costCacheReadUsd: 0, costInputUsd: 0, costOutputUsd: 0,
    durationMs: 0,
};
// ============================================================================
// ChatPage
// ============================================================================
export default function ChatPage() {
    // The new sidebar layout only puts the sessionId in the URL; the active
    // project comes from the Workspace's outlet context. Legacy /p/:projectId
    // routes still expose both via useParams as a fallback.
    const params = useParams();
    const sessionId = params.sessionId;
    const ctx = useOutletContext();
    const projectId = params.projectId ?? ctx?.project?.id ?? "";
    // The Workspace sidebar puts a hamburger icon at the top-left when it's
    // collapsed. The chat header needs to reserve space for it whenever the
    // sidebar is NOT visible (so its content doesn't get hidden behind the
    // icon). Default to "reserve space" if context isn't available (legacy
    // standalone routes use this path).
    const needsHamburgerSpace = ctx ? !ctx.sidebarOpen : true;
    // Admin-only debug + LLM-trace surfaces. The corresponding /api endpoints
    // ALSO enforce admin role on the server, so hiding the buttons here is
    // purely a UX guard (the network call would 403 anyway for non-admins).
    const isAdmin = ctx?.isAdmin ?? false;
    // Shared session store — lets the WS handler update the sidebar's
    // session name (and any other consumers) via a normal React state
    // write. No custom events, no polling.
    const sessions = useSessions();
    const [turns, setTurns] = useState([]);
    const [currentTurn, setCurrentTurn] = useState(null);
    const [plan, setPlan] = useState([]);
    const [git, setGit] = useState(null);
    const [pushing, setPushing] = useState(false);
    const [wsStatus, setWsStatus] = useState("connecting");
    // App-wide theme. Same hook used by Layout's header; the chat page renders
    // its own header (so it can show the WS status / debug pill) and therefore
    // needs to surface the toggle here too.
    const { effective: themeEffective, toggle: toggleTheme } = useTheme();
    const [loadErr, setLoadErr] = useState(null);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    // True while we're mid-cancel — i.e. the user hit Enter / Send while a
    // turn was already in flight and we're awaiting the cancel-then-send
    // handoff to the server. The Send button morphs into "Stopping…" +
    // a spinner so the user knows their input was received (they're
    // NOT being ignored) and that the in-flight task is being aborted
    // before the new one starts. Without this, the user could think
    // their new message went into a void for the 1-2 seconds it takes
    // the cancel to land and the new turn to begin streaming.
    const [interrupting, setInterrupting] = useState(false);
    // Mirror of `sending` in a ref so the async send() loop can poll
    // the latest value (React's setState is asynchronous; reading the
    // `sending` state inside the loop would always see the stale
    // snapshot from when send() was called). The ref is kept in sync
    // via a useEffect below.
    const sendingRef = useRef(false);
    const scrollRef = useRef(null);
    // Ref to the chat composer textarea. Used by the auto-grow effect
    // (lines below) to grow the box as the user types, capped at
    // MAX_COMPOSER_HEIGHT so a 50-line paste doesn't take over the screen.
    const composerRef = useRef(null);
    // Keep sendingRef in sync with the `sending` state. Used by send()
    // below to poll the latest value across an async boundary (React
    // state reads inside an async function always see the snapshot
    // from when the function was called, not the latest value).
    useEffect(() => { sendingRef.current = sending; }, [sending]);
    // -------------------------------------------------------------------------
    // Composer auto-grow.
    //
    // The chat composer starts as a 1-line input but expands as the user types
    // — Claude.ai-style — capped at MAX_COMPOSER_HEIGHT (6 lines @ 24 px line
    // height = 144 px). Above the cap, the textarea scrolls internally so
    // pasting a 50-line stack trace doesn't take over the screen.
    //
    // Why useLayoutEffect and not useEffect: we need the height applied
    // BEFORE the browser paints the new value, otherwise the textarea
    // briefly shows the old height with the new content overflowing.
    // Reading scrollHeight forces a synchronous layout, which is exactly
    // what useLayoutEffect is for.
    //
    // The effect depends on `input` so it runs on every keystroke (and
    // on history ↑/↓ recall, which mutates `input` programmatically).
    // -------------------------------------------------------------------------
    useLayoutEffect(() => {
        const el = composerRef.current;
        if (!el)
            return;
        // Reset to auto first so a SHRINK (e.g. after a multi-line paste
        // gets deleted) measures the natural height, not the previous
        // explicit height.
        el.style.height = "auto";
        const next = Math.min(el.scrollHeight, MAX_COMPOSER_HEIGHT);
        el.style.height = `${next}px`;
        // Internal scroll only when the content actually exceeds the cap.
        el.style.overflowY = el.scrollHeight > MAX_COMPOSER_HEIGHT ? "auto" : "hidden";
    }, [input]);
    // Prefill from the home screen's "Try saying" cards. The Workspace
    // page creates a new session, then navigates here with
    // `state: { prefill: "Build me a to-do list app." }` (or whichever
    // card the user clicked). We pick it up once on mount and pop it
    // into the composer input — the user still has to hit send, which
    // is intentional: clicking a starter card should land them in a
    // chat that's ready to send, not in one that's already burning
    // a turn. We strip `prefill` from location.state afterwards so a
    // re-render (or back-button + forward) doesn't re-apply it. The
    // useLocation reference is captured as a const so the empty
    // dep-array effect doesn't trigger React's "missing dep" warning
    // — and so a navigation that DOESN'T carry a prefill (typing the
    // session URL directly, refreshing) leaves `input` alone.
    const location = useLocation();
    useEffect(() => {
        const prefill = location.state?.prefill;
        if (typeof prefill === "string" && prefill.length > 0) {
            setInput(prefill);
            // Strip the state so re-renders / hot-reload don't re-prefill
            // on top of whatever the user has since typed. `replace: true`
            // avoids polluting browser history with two entries.
            window.history.replaceState({}, "");
        }
        // location is intentionally not in the dep array — re-running
        // on every location change would clobber user edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Deployed apps from THIS session — populated on mount from
    // /api/sessions/<id>/deployed-apps, refreshed when the user deploys a
    // new sub-app via the Deploy button or deletes one from the strip.
    const [deployedApps, setDeployedApps] = useState([]);
    // Live deploy progress per slug. Keyed by slug; set by the
    // deploy_progress WS event so the matching pill can show a
    // "Step 3/12 — Building…" chip instead of just "Deploying…".
    // Cleared by deploy_complete / deploy_failed.
    const [deployProgress, setDeployProgress] = useState({});
    // The slug the user just clicked Update on for THIS session.
    // deploy_progress events don't carry a slug (the backend runs
    // one deploy at a time per session), so we attribute progress
    // events to this ref. Cleared when the session changes.
    const lastClickedUpdateSlugRef = useRef(null);
    // Debounce handle for the live "/detected-dist" re-fetch. The
    // build banner + per-pill "Update" button read from
    // `lastDetected`, but the original code only re-fetched it on
    // turn_summary + deploy_complete — so a 10-step `npm run build`
    // could finish, the turn end, and the Update button wouldn't
    // appear until the next refresh. Triggering a re-fetch on every
    // file_changed (for files that look like a dist build artifact) +
    // every bash tool_done is correct but chatty, so we debounce
    // 500ms after the last trigger — N events collapse into ONE
    // /detected-dist GET, so a long build costs exactly one server
    // scan at the end. Cleared on session switch.
    const detectedDistRefreshTimerRef = useRef(null);
    // "Build ready" detection — set when the session has a built dist
    // newer than the most recent deploy FROM this session. The chat
    // shows a banner under the agent's last turn when true, and the
    // banner's Deploy button opens the same dialog as the strip's.
    const [lastDetected, setLastDetected] = useState(null);
    // The Deploy modal lives in the DeployStrip; this flag is what the
    // build-ready banner flips on to open the same modal.
    const [showDeployModal, setShowDeployModal] = useState(false);
    // Context-used progress for the bottom-of-chat bar. Updated by `context_update`
    // WS events. 0 means "no data yet" (don't render the bar).
    const [contextUsed, setContextUsed] = useState(0);
    const [contextCompacting, setContextCompacting] = useState(false);
    // Auto-compact threshold (50K default) — the chip shows "X% used"
    // against this denominator.
    const [contextThreshold, setContextThreshold] = useState(50_000);
    // Most recent prompt-cache split. The chip's tooltip uses these
    // to show "X new · Y cache hits" so a 76k chip can be read as
    // "7k new, 69k cache hits" — without this, a long session
    // running at 95% cache hit rate looks like 95% context used
    // when really only 5% is fresh. 0 / 0 when the provider
    // doesn't surface cache fields.
    const [contextCacheRead, setContextCacheRead] = useState(0);
    const [contextCacheCreation, setContextCacheCreation] = useState(0);
    // Auto-compact breadcrumb list. Each entry corresponds to a `context_compacted`
    // WS event and renders as a collapsible system message in the transcript
    // so the user can see what was summarised when. Cleared on session switch.
    const [contextCompactedNotes, setContextCompactedNotes] = useState([]);
    // Follow-mode for the chat scroll: stick to bottom while new events
    // arrive UNLESS the user scrolls up to read. If they do, leave them where
    // they are and pop a floating "↓" button so they can catch back up on a
    // single tap. The button hides automatically when they reach the bottom
    // again (either by scrolling or by tapping it).
    const [chatAtBottom, setChatAtBottom] = useState(true);
    const onChatScroll = () => {
        const el = scrollRef.current;
        if (!el)
            return;
        const threshold = 60; // px from bottom counted as "still at bottom"
        setChatAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < threshold);
    };
    const jumpChatToBottom = () => {
        const el = scrollRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
        setChatAtBottom(true);
    };
    // ---- Debug stream — last N raw WS events so we can SEE what's flowing.
    // Toggle via the bug icon in the header; persists across reloads so it stays
    // on while we're triaging the live-streaming issue.
    const [debugOpen, setDebugOpen] = useState(() => {
        try {
            return localStorage.getItem("debug_stream") === "1";
        }
        catch {
            return false;
        }
    });
    // LLM trace panel toggle (⌥ llm button).
    const [llmTraceOpen, setLlmTraceOpen] = useState(false);
    const [debugEvents, setDebugEvents] = useState([]);
    // Track the last-seen event timestamp (in ms) so we can refetch any
    // events the WebSocket missed during the load gap. Without this, an
    // event emitted by the backend AFTER /events returns but BEFORE the
    // WS subscription opens would silently disappear from the UI.
    // Tracked across BOTH the initial events load AND every incoming
    // live event. The WS-open handler uses it to ask for "anything since
    // this timestamp" and replays the gap through handleEvent.
    const lastEventTsRef = useRef(0);
    const toggleDebug = () => {
        setDebugOpen((v) => {
            const next = !v;
            try {
                localStorage.setItem("debug_stream", next ? "1" : "0");
            }
            catch { }
            return next;
        });
    };
    // ---- Initial reconstruction from history + events ---------------------
    useEffect(() => {
        if (!sessionId)
            return;
        // Ref the pill setTimeout writes so deploy_progress events
        // (which don't carry a slug) can be attributed to the slug the
        // user just clicked Update on. Resets when the session changes.
        lastClickedUpdateSlugRef.current = null;
        // Cancel any pending debounced /detected-dist re-fetch from a
        // previous session — we don't want a stale timer from session A
        // firing setLastDetected for session B.
        if (detectedDistRefreshTimerRef.current) {
            clearTimeout(detectedDistRefreshTimerRef.current);
            detectedDistRefreshTimerRef.current = null;
        }
        setDeployProgress({});
        // HARD RESET all per-session state synchronously BEFORE any async load.
        // Without this, switching from session A to session B mid-turn left A's
        // currentTurn (with isStreaming=true) in state — the Stop button kept
        // showing on B, but clicking it cancelled B (the URL's sessionId), not
        // A. Same leak hit previewUrl (A's preview banner showed on B), plan,
        // git, debugEvents, sending, and loadErr. Resetting all of them up
        // front guarantees the new session starts from a clean slate; the
        // load below then populates fresh data from B's event history.
        setTurns([]);
        setCurrentTurn(null);
        setPlan([]);
        setGit(null);
        setDeployedApps([]); // wipe previous session's deploy strip
        setSending(false);
        setLoadErr(null);
        setDebugEvents([]);
        setWsStatus("connecting");
        lastEventTsRef.current = 0;
        // Reset the chip's per-session state. The new session's WS connect
        // event will re-populate these from the server's persisted value.
        setContextUsed(0);
        setContextCompacting(false);
        setContextCacheRead(0);
        setContextCacheCreation(0);
        setContextCompactedNotes([]);
        let cancelled = false;
        (async () => {
            try {
                const [events, gitInfo, sessionInfo] = await Promise.all([
                    sessionApi.events(sessionId),
                    sessionApi.git(sessionId).catch(() => null),
                    sessionsApi.get(sessionId).catch(() => null),
                ]);
                if (cancelled)
                    return;
                if (gitInfo)
                    setGit(gitInfo);
                // Seed the context chip from the session's persisted
                // `last_context_used` so the user doesn't see "0% used" flash
                // for a frame on refresh / session switch. The WS
                // `context_update` event (or the persisted value on WS connect)
                // will overwrite this if the LLM fires again.
                if (sessionInfo?.last_context_used) {
                    setContextUsed(Number(sessionInfo.last_context_used));
                }
                // Walk the event log in chronological order, folding into turns +
                // (if the log ends mid-turn) an in-progress currentTurn. Setting
                // currentTurn from the rebuild is what makes streaming RESUME on
                // refresh / session-switch — live WS events arriving after this
                // point have a non-null target to update.
                const { turns: rebuilt, currentTurn: rebuiltCurrent, plan: replayedPlan } = rebuildTranscript(events.map((e) => ({
                    kind: e.kind, payload: e.payload, ts: e.created_at * 1000,
                })));
                setTurns(rebuilt);
                setCurrentTurn(rebuiltCurrent);
                setPlan(replayedPlan);
                // Rebuild the auto-compact breadcrumb list from history. The
                // live WS handler adds cards as `context_compacted` events
                // arrive, but on a fresh page load (or a refresh that landed
                // AFTER compactions have already happened) the events table
                // has all the compactions that fired during the session —
                // if we don't replay them here, the user sees a session
                // where the chat chip says "compacted N times" but ZERO
                // breadcrumb cards. Each historical event maps to a
                // ContextCompactedNote with the same shape the WS handler
                // uses; identical IDs ensure a follow-up live event for the
                // same compaction (impossible in practice — compactions
                // don't double-fire) wouldn't double-render.
                const historicalCompactions = events
                    .filter((e) => e.kind === "context_compacted")
                    .map((e) => {
                    const p = (e.payload ?? {});
                    return {
                        id: `compact-${e.created_at * 1000}-${Number(p.removed ?? 0)}-${Number(p.kept ?? 0)}`,
                        ts: e.created_at * 1000,
                        removed: Number(p.removed ?? 0),
                        kept: Number(p.kept ?? 0),
                        tokensBefore: Number(p.tokens_before ?? 0),
                        tokensAfter: Number(p.tokens_after ?? 0),
                        summaryPreview: String(p.summary_preview ?? ""),
                        threshold: Number(p.threshold ?? 0),
                    };
                })
                    .filter((n) => n.removed > 0); // matches the WS-side guard
                setContextCompactedNotes(historicalCompactions);
                // Load this session's deployed apps so the strip renders correctly
                // on every mount (refresh / session switch). Failure is non-fatal —
                // strip just shows empty.
                sessionApi.deployedApps(sessionId).then(setDeployedApps).catch(() => { });
                // Also load detected-dist so the build-ready banner knows whether
                // to render. Same non-fatal failure mode.
                sessionApi.detectedDist(sessionId).then(setLastDetected).catch(() => { });
                // Record the watermark for the WS catchup. Use the latest event's
                // created_at in SECONDS (backend's column unit) so the next
                // ?since=<ts> query returns ONLY events newer than what we already
                // have. Falls back to 0 (full replay) on a fresh session.
                if (events.length > 0) {
                    lastEventTsRef.current = events[events.length - 1].created_at;
                }
                // Session rename catch-up. If the live WS missed the
                // session_renamed event (e.g. user came back to this tab after
                // the rename fired), the latest one is in the event history —
                // replay it through the shared context so the sidebar stays in
                // sync. The rename() call is a no-op if the name hasn't
                // changed since the last write.
                const lastRename = [...events]
                    .reverse()
                    .find((e) => e.kind === "session_renamed");
                if (lastRename) {
                    const newName = String(lastRename.payload.new_name ?? "");
                    if (newName)
                        sessions.rename(sessionId, newName);
                }
            }
            catch (e) {
                if (!cancelled)
                    setLoadErr(e?.message ?? "failed to load history");
            }
        })();
        return () => { cancelled = true; };
    }, [sessionId]);
    // ---- WebSocket subscription -------------------------------------------
    useEffect(() => {
        if (!sessionId)
            return;
        // Capture the sessionId this subscription was bound to in a local
        // const. If the user switches sessions, the cleanup below closes the
        // socket — but a message already in-flight (between WebSocket close()
        // and the next event-loop tick) could still fire its callback after
        // sessionId has changed. The boundSid guard makes those late events a
        // no-op so they can't bleed into the NEW session's state.
        const boundSid = sessionId;
        const handle = openEventStream(sessionId, (ev) => {
            // Reject any event delivered after we navigated away from this
            // session. The dependency-array invariant tells React this effect
            // belongs to `boundSid`; if the URL's sessionId has drifted, ignore.
            if (boundSid !== sessionId)
                return;
            // Advance the watermark so the next WS-reconnect catchup query
            // only refetches truly-newer events. Live event ts is in MS;
            // convert to SECONDS to match the backend column unit. Math.floor
            // ensures we don't accidentally request the same event again on
            // a sub-second precision mismatch.
            const evSecs = Math.floor(ev.ts / 1000);
            if (evSecs > lastEventTsRef.current) {
                lastEventTsRef.current = evSecs;
            }
            // Tap every event into the debug stream FIRST so we capture it even
            // if handleEvent throws / drops it (which is what we're trying to
            // debug). Keep last 200 entries.
            setDebugEvents((prev) => {
                const next = [...prev, { kind: ev.kind, payload: ev.payload, ts: ev.ts }];
                return next.length > 200 ? next.slice(-200) : next;
            });
            handleEvent(ev);
        }, (s) => {
            if (boundSid !== sessionId)
                return;
            setWsStatus(s);
            // ---- Race-window catchup ------------------------------------------
            // Every time the WS transitions to "open" (initial connect AND any
            // reconnect after network blip), fetch any events the backend
            // emitted while we weren't subscribed. The watermark `lastEventTsRef`
            // is updated by the initial events load + every incoming WS event,
            // so this query returns only the gap. Replay each through
            // handleEvent so they update currentTurn / turns just like a live
            // event would. Without this, an assistant_text emitted during the
            // ~50–200ms gap between the events HTTP fetch and the WS handshake
            // would be silently lost — the user would see a frozen turn and
            // think streaming broke.
            if (s === "open" && lastEventTsRef.current > 0) {
                sessionApi.events(sessionId, lastEventTsRef.current)
                    .then((missed) => {
                    if (boundSid !== sessionId)
                        return; // navigated away mid-fetch
                    for (const ev of missed) {
                        const live = {
                            kind: ev.kind, payload: ev.payload, ts: ev.created_at * 1000,
                        };
                        const evSecs = ev.created_at;
                        if (evSecs > lastEventTsRef.current) {
                            lastEventTsRef.current = evSecs;
                        }
                        handleEvent(live);
                    }
                })
                    .catch(() => { });
            }
        });
        return () => handle.close();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);
    // ---- Live /detected-dist refresh --------------------------------------
    // The build banner + per-pill "Update" button read from
    // `lastDetected`, but the original code only re-fetched it on
    // turn_summary + deploy_complete. So a 10-step `npm run build`
    // could finish, the turn end, and the Update button wouldn't
    // appear until the next refresh. Triggered on:
    //   - file_changed whose path looks like a dist build artifact
    //     (rare — agents usually edit source, but a deliberate edit
    //     of dist/index.html or dist/assets/* should still update live)
    //   - tool_done for bash commands (covers `npm run build` and
    //     every other build tool that writes through the shell)
    // Debounced 500ms after the last trigger so a 10-event build
    // collapses into ONE server scan. The endpoint is cheap (O(workspace
    // scan)) so this is the lowest-risk place to be "always live".
    function scheduleDetectedDistRefresh(reason) {
        if (!sessionId)
            return;
        if (detectedDistRefreshTimerRef.current) {
            clearTimeout(detectedDistRefreshTimerRef.current);
        }
        detectedDistRefreshTimerRef.current = setTimeout(() => {
            detectedDistRefreshTimerRef.current = null;
            const sid = sessionId;
            sessionApi
                .detectedDist(sid)
                .then((d) => {
                // Race guard: sessionId may have changed while the
                // fetch was in flight (user navigated to a different
                // session). The next setLastDetected would clobber the
                // NEW session's value, so check first.
                if (sid !== sessionId)
                    return;
                setLastDetected(d);
            })
                .catch(() => { });
        }, 500);
    }
    // Apply ONE event to current state. Pure function over (turns, currentTurn,
    // plan) — but expressed as setter calls because React state is split.
    function handleEvent(ev) {
        const p = ev.payload;
        switch (ev.kind) {
            // --- Plan (turn-independent) ---
            case "todo_update": {
                const items = Array.isArray(p.items) ? p.items : [];
                setPlan(items);
                return;
            }
            // --- Turn lifecycle ---
            case "user_message": {
                setCurrentTurn((curr) => {
                    if (curr && curr.userPrompt === String(p.text) && curr.isStreaming) {
                        return curr; // dedupe — backend echo + our optimistic add
                    }
                    return makeTurn(String(p.text ?? ""), ev.ts);
                });
                return;
            }
            case "assistant_text": {
                const text = String(p.text ?? "");
                const done = !!p.done;
                setCurrentTurn((curr) => {
                    if (!curr)
                        return curr;
                    if (done) {
                        // Final flush — canonical tag-stripped text. Replace the most-recent
                        // text block (so we don't double-render the streamed answer) AND
                        // replace the legacy assistantText field for stats / debug parity.
                        return {
                            ...curr,
                            assistantText: text || curr.assistantText,
                            blocks: text
                                ? _replaceLastTextBlock(curr.blocks, text, ev.ts)
                                : curr.blocks,
                            isStreaming: false,
                        };
                    }
                    return {
                        ...curr,
                        assistantText: curr.assistantText + text,
                        blocks: _appendTextChunk(curr.blocks, "text", text, ev.ts),
                        isStreaming: true,
                    };
                });
                return;
            }
            case "context_update": {
                // Server publishes this after every LLM call. `used_tokens` is
                // the NEW (uncached + writes) tokens the model actually
                // processed this turn — the chip's "% used" denominator
                // matches the real context burden (NOT the inflated total
                // including cache hits, which would have a "hi" turn read
                // as 76k because the static system prompt is cache-served
                // on every call). `threshold` (50K default) is the
                // auto-compact threshold.
                //
                // We also surface `cache_read` + `cache_creation` so the
                // chip tooltip can show "X new, Y cache hits" — without
                // that split, the user can't tell whether a high
                // percentage is genuine context pressure or just the
                // system prompt being re-counted from cache every turn.
                const used = Number(p.used_tokens ?? 0);
                const threshold = Number(p.threshold ?? 0);
                const cacheRead = Number(p.cache_read ?? 0);
                const cacheCreation = Number(p.cache_creation ?? 0);
                if (used <= 0)
                    return;
                if (threshold > 0)
                    setContextThreshold(threshold);
                setContextUsed(used);
                setContextCompacting(Boolean(p.compacting));
                setContextCacheRead(cacheRead);
                setContextCacheCreation(cacheCreation);
                return;
            }
            case "context_compacted": {
                // Chat-visible breadcrumb when auto-compaction fires. Renders
                // inline in the turn during which it fired (so the user sees
                // the 📦 card at the moment their chat got summarised, not
                // stacked at the bottom of the transcript). We also keep a
                // parent-level list as a fallback for the case where no
                // turn is active (e.g. a compact that fires between turns).
                const removed = Number(p.removed ?? 0);
                if (removed <= 0)
                    return;
                const note = {
                    id: `compact-${ev.ts}-${removed}-${Number(p.kept ?? 0)}`,
                    ts: ev.ts,
                    removed,
                    kept: Number(p.kept ?? 0),
                    tokensBefore: Number(p.tokens_before ?? 0),
                    tokensAfter: Number(p.tokens_after ?? 0),
                    summaryPreview: String(p.summary_preview ?? ""),
                    threshold: Number(p.threshold ?? 0),
                };
                // Attach the 📦 card to the LAST COMPLETED TURN, not the
                // just-started currentTurn. The backend's `context_compacted`
                // event fires PRE-LLM inside `node_agent`: by the time it
                // reaches the WS, the user has already submitted the new
                // prompt, so currentTurn = the NEW turn. But the *summarised*
                // history belongs to the PREVIOUS turn's response — the
                // one whose context just barely crossed the threshold. The
                // user wants to see the card at the bottom of that prior
                // turn (right after its final response / tool calls), not
                // at the top of the new turn they just typed.
                //
                // Why this is the right place semantically:
                //   - The compact happens because the *previous* turn's
                //     accumulated tool calls + reasoning pushed context
                //     over the threshold. The previous turn is what got
                //     "squeezed out".
                //   - The new turn just inherits the compacted context;
                //     the compact event isn't really about it.
                //   - Reading top-to-bottom, the user sees their prior
                //     turn end with "📦 auto-compacted: summarised 80
                //     older messages" — a natural narrative beat.
                let attached = false;
                setTurns((prev) => {
                    if (prev.length === 0)
                        return prev;
                    attached = true;
                    const last = prev[prev.length - 1];
                    return [
                        ...prev.slice(0, -1),
                        { ...last, compactNotes: [...(last.compactNotes ?? []), note] },
                    ];
                });
                if (!attached) {
                    // No completed turns yet — this is the first user prompt
                    // ever and the very first call already crossed the
                    // threshold (possible on a rehydrate from a previously-
                    // long session). Fall back to attaching on currentTurn
                    // so the card has SOME home; the user sees it inline in
                    // the first turn rather than losing it to the orphan
                    // fallback.
                    setCurrentTurn((curr) => curr ? { ...curr, compactNotes: [...(curr.compactNotes ?? []), note] } : curr);
                }
                // Keep the legacy parent-level list in sync too — it's
                // displayed (a) as a fallback when the parent-level rerender
                // is faster than the turn's rerender and (b) for orphan
                // compactions that somehow don't have a turn to attach to.
                setContextCompactedNotes((prev) => [...prev, note]);
                // Sync the chip with the post-compact reality. The next
                // `context_update` event for the next LLM call will land
                // shortly with the real input_tokens (which is what the
                // server-side LLM actually saw) and overwrite this — but in
                // the gap (often 1-3s for a busy model), showing the local
                // post-compact estimate is the only way the chip can
                // truthfully say "compact fired → context dropped from X
                // to Y". tokens_after is the post-compact local estimate
                // (same number the live chip would have shown if we'd been
                // able to ship the post-compact prompt instantly).
                const tokensAfter = Number(p.tokens_after ?? 0);
                if (tokensAfter > 0) {
                    setContextUsed(tokensAfter);
                }
                // Visual "compacting…" pulse on the chip for ~1.2s so the
                // user sees a visible ack of the compact — settles naturally
                // when the next context_update lands.
                setContextCompacting(true);
                setTimeout(() => {
                    setContextCompacting(false);
                }, 1200);
                return;
            }
            case "token_update": {
                // Live in/out token deltas — every model call inside a turn emits
                // ONE token_update after it completes (see _stream_model_call in
                // agents/nodes.py). We use that fact to surface PER-CALL token usage
                // in the UI, not just the running total: each event either pushes a
                // chronological `llm_call` block (orchestrator) or appends an
                // `LlmCall` entry inside the sub-agent that issued it.
                const inDelta = Number(p.input_delta ?? 0);
                const outDelta = Number(p.output_delta ?? 0);
                const cacheR = Number(p.cache_read_delta ?? 0);
                const cacheW = Number(p.cache_creation_delta ?? 0);
                const aid = typeof p.agent_id === "string" ? p.agent_id : "";
                if (inDelta === 0 && outDelta === 0 && cacheR === 0 && cacheW === 0)
                    return; // skip zero-deltas
                setCurrentTurn((curr) => {
                    if (!curr)
                        return curr;
                    if (aid && curr.agents[aid]) {
                        const a = curr.agents[aid];
                        return {
                            ...curr,
                            agents: {
                                ...curr.agents,
                                [aid]: {
                                    ...a,
                                    liveInputTokens: a.liveInputTokens + inDelta,
                                    liveOutputTokens: a.liveOutputTokens + outDelta,
                                    llmCalls: [...a.llmCalls, {
                                            ts: ev.ts, inputTokens: inDelta, outputTokens: outDelta,
                                            cacheReadTokens: cacheR, cacheCreationTokens: cacheW,
                                        }],
                                },
                            },
                        };
                    }
                    return {
                        ...curr,
                        liveInputTokens: curr.liveInputTokens + inDelta,
                        liveOutputTokens: curr.liveOutputTokens + outDelta,
                        blocks: [...curr.blocks, {
                                id: _newBlockId("llm_call", ev.ts, curr.blocks.length),
                                kind: "llm_call", ts: ev.ts,
                                inputTokens: inDelta, outputTokens: outDelta,
                                cacheReadTokens: cacheR, cacheCreationTokens: cacheW,
                            }],
                    };
                });
                return;
            }
            case "thinking_text": {
                // Model reasoning chunk — appended to the chronological timeline as
                // its own block kind so it renders distinct from the visible answer.
                const text = String(p.text ?? "");
                if (!text)
                    return;
                setCurrentTurn((curr) => curr ? {
                    ...curr,
                    thinkingText: curr.thinkingText + text,
                    blocks: _appendTextChunk(curr.blocks, "thinking", text, ev.ts),
                } : curr);
                return;
            }
            case "turn_summary": {
                const summary = {
                    tools_used: Number(p.tools_used ?? 0),
                    duration_ms: Number(p.duration_ms ?? 0),
                    input_tokens: Number(p.input_tokens ?? 0),
                    output_tokens: Number(p.output_tokens ?? 0),
                    cache_read_tokens: Number(p.cache_read_tokens ?? 0),
                    cache_write_tokens: Number(p.cache_write_tokens ?? 0),
                    cost_usd: Number(p.cost_usd ?? 0),
                };
                setCurrentTurn((curr) => {
                    if (!curr)
                        return null;
                    const finished = { ...curr, summary, isStreaming: false };
                    setTurns((ts) => [...ts, finished]);
                    return null;
                });
                // Re-poll detected-dist right after the turn ends so the
                // build-ready banner appears under the new turn (the agent
                // just finished writing files, the dist is fresh). Cheap
                // call; the WS has no equivalent.
                if (sessionId) {
                    sessionApi.detectedDist(sessionId).then(setLastDetected).catch(() => { });
                }
                // Background auto-rename happens 0–6s after turn_summary. Refetch
                // the session once after a short delay so the sidebar picks up
                // the LLM-generated name without needing the WS event. Single
                // poll — the WS event is the primary path; this is just a
                // cheap belt-and-suspenders for mobile/PWA cases.
                if (sessionId) {
                    const sid = sessionId;
                    setTimeout(() => {
                        sessionsApi.get(sid).then((s) => sessions.rename(sid, s.name)).catch(() => { });
                    }, 2500);
                }
                return;
            }
            case "session_renamed": {
                // The server (background LLM-suggested rename or our own PATCH
                // path) changed this session's display name. Update the shared
                // SessionContext — the sidebar (Workspace) and session list
                // (SessionList page) re-render automatically because they read
                // from the same context.
                const newName = String(p.new_name ?? "");
                if (newName && sessionId) {
                    sessions.rename(sessionId, newName);
                    if (p.was_suffixed) {
                        const prev = String(p.previous_name ?? "");
                        if (prev && prev !== newName) {
                            sessions.setToast({
                                message: `Renamed to "${newName}" — "${prev}" was already taken.`,
                            });
                        }
                    }
                }
                return;
            }
            case "error": {
                // Error means the turn is over — clear isStreaming RIGHT NOW so the
                // elapsed timer / "Thinking…" / streaming dot stop instantly. We
                // also expect a turn_summary to follow (backend sends both on the
                // error path now), but don't wait for it to freeze the UI.
                const msg = String(p.message ?? "unknown error");
                setCurrentTurn((curr) => curr ? {
                    ...curr, error: msg, isStreaming: false,
                } : curr);
                return;
            }
            // --- Tool stream ---
            case "tool_start": {
                const tool = {
                    id: `${ev.ts}-${p.tool}-${Math.random().toString(36).slice(2, 6)}`,
                    tool: String(p.tool ?? "?"),
                    target: typeof p.target === "string" ? p.target : undefined,
                    status: "running",
                    startedAt: ev.ts,
                };
                const aid = typeof p.agent_id === "string" ? p.agent_id : "";
                setCurrentTurn((curr) => {
                    if (!curr)
                        return curr;
                    if (aid && curr.agents[aid]) {
                        // Sub-agent's tool — nest under that agent (no top-level block).
                        return {
                            ...curr,
                            agents: {
                                ...curr.agents,
                                [aid]: {
                                    ...curr.agents[aid],
                                    tools: [...curr.agents[aid].tools, tool],
                                },
                            },
                        };
                    }
                    // Orchestrator-level tool: append to the flat list AND push a block.
                    return {
                        ...curr,
                        tools: [...curr.tools, tool],
                        blocks: [...curr.blocks, {
                                id: _newBlockId("tool", ev.ts, curr.blocks.length),
                                kind: "tool", toolId: tool.id, ts: ev.ts,
                            }],
                    };
                });
                return;
            }
            case "tool_done": {
                const toolName = String(p.tool ?? "?");
                const isError = !!p.error;
                const preview = typeof p.preview === "string" ? p.preview : undefined;
                const previewTruncated = !!p.preview_truncated;
                const aid = typeof p.agent_id === "string" ? p.agent_id : "";
                // Parse the smart-truncation marker the bash tool embeds in its
                // preview text. Format (set in tools/wrappers.py):
                //   …truncated N chars; this was a SUCCESS/FAILURE.
                //   # truncation: kept_first=X, kept_last=Y, dropped=Z, total=T
                //   Full output saved to `/path`.
                //   …
                // We keep the parsing tolerant — the marker may evolve. If any
                // field is missing we still surface "truncated" but with zeros.
                const bashOutput = parseBashOutputMarker(preview);
                setCurrentTurn((curr) => {
                    if (!curr)
                        return curr;
                    const patchToolList = (list) => {
                        const next = [...list];
                        for (let i = next.length - 1; i >= 0; i--) {
                            if (next[i].tool === toolName && next[i].status === "running") {
                                next[i] = {
                                    ...next[i],
                                    status: isError ? "error" : "done",
                                    preview,
                                    previewTruncated,
                                    // Only set bashOutput on bash calls (parser returns
                                    // null for non-bash previews), so the UI's status
                                    // line only appears for bash tool results.
                                    bashOutput: bashOutput ?? next[i].bashOutput,
                                    endedAt: ev.ts,
                                };
                                break;
                            }
                        }
                        return next;
                    };
                    if (aid && curr.agents[aid]) {
                        return {
                            ...curr,
                            agents: {
                                ...curr.agents,
                                [aid]: {
                                    ...curr.agents[aid],
                                    tools: patchToolList(curr.agents[aid].tools),
                                },
                            },
                        };
                    }
                    return { ...curr, tools: patchToolList(curr.tools) };
                });
                // Live Update-button refresh: bash tool_done covers the common
                // case of `npm run build` / `vite build` / `next build` / etc.
                // rewriting dist/. Source-file edits route through
                // file_changed below instead, so this only fires for shell
                // invocations — which is exactly where the dist mtime actually
                // changes. Debounced 500ms via scheduleDetectedDistRefresh.
                if (toolName === "bash" && !aid) {
                    scheduleDetectedDistRefresh("tool_done:bash");
                }
                return;
            }
            // --- File diffs ---
            case "file_changed": {
                const fc = {
                    id: `${p.path}-${ev.ts}`,
                    path: String(p.path ?? ""),
                    kind: p.kind === "create" ? "create" : "edit",
                    diff: String(p.diff ?? ""),
                    bytes: Number(p.bytes ?? 0),
                    ts: ev.ts,
                };
                setCurrentTurn((curr) => curr ? {
                    ...curr,
                    fileChanges: [...curr.fileChanges, fc],
                    blocks: [...curr.blocks, {
                            id: _newBlockId("file", ev.ts, curr.blocks.length),
                            kind: "file", file: fc,
                        }],
                } : curr);
                // Live Update-button refresh: a file_changed that lands inside
                // a dist build output (e.g. a deliberate edit of
                // dist/index.html, or a tool the agent uses to write built
                // assets) should bump the candidate's mtime the same way
                // `npm run build` would. The check is intentionally narrow
                // — /dist/ or /build/ segments in the path — to avoid
                // triggering a scan for every source-file edit (those
                // typically don't change dist mtime, and turn_summary's
                // re-fetch covers them). Debounced 500ms.
                const fcPath = fc.path;
                if (fcPath &&
                    (fcPath.includes("/dist/") || fcPath.includes("/build/") ||
                        fcPath.endsWith("/dist") || fcPath.endsWith("/build"))) {
                    scheduleDetectedDistRefresh("file_changed:dist");
                }
                return;
            }
            // --- Sub-agents ---
            case "agent_spawn": {
                const r = {
                    agent_id: String(p.agent_id ?? ""),
                    description: String(p.description ?? ""),
                    subagent_type: String(p.subagent_type ?? "general-purpose"),
                    name: String(p.name ?? ""),
                    model: String(p.model ?? ""),
                    status: "running",
                    output_file: "",
                    error: "",
                    spawned_at: ev.ts,
                    updated_at: ev.ts,
                    tools: [],
                    liveInputTokens: 0,
                    liveOutputTokens: 0,
                    llmCalls: [],
                };
                setCurrentTurn((curr) => curr ? {
                    ...curr,
                    agents: { ...curr.agents, [r.agent_id]: r },
                    blocks: [...curr.blocks, {
                            id: _newBlockId("agent", ev.ts, curr.blocks.length),
                            kind: "agent", agentId: r.agent_id, ts: ev.ts,
                        }],
                } : curr);
                return;
            }
            case "agent_status_update": {
                const id = String(p.agent_id ?? "");
                setCurrentTurn((curr) => {
                    if (!curr)
                        return curr;
                    const existing = curr.agents[id];
                    if (!existing)
                        return curr;
                    return {
                        ...curr,
                        agents: {
                            ...curr.agents,
                            [id]: {
                                ...existing,
                                status: String(p.status ?? existing.status),
                                output_file: String(p.output_file ?? existing.output_file),
                                error: String(p.error ?? ""),
                                updated_at: ev.ts,
                            },
                        },
                    };
                });
                return;
            }
            // --- Commits / push ---
            case "commit_made": {
                const cr = {
                    id: `${p.sha}-${ev.ts}`,
                    sha: String(p.sha ?? ""),
                    branch: String(p.branch ?? ""),
                    message: String(p.message ?? ""),
                    files: Array.isArray(p.files) ? p.files : [],
                    ts: ev.ts,
                };
                setCurrentTurn((curr) => curr ? {
                    ...curr,
                    commits: [...curr.commits, cr],
                    blocks: [...curr.blocks, {
                            id: _newBlockId("commit", ev.ts, curr.blocks.length),
                            kind: "commit", commit: cr,
                        }],
                } : curr);
                if (sessionId)
                    sessionApi.git(sessionId).then(setGit).catch(() => { });
                return;
            }
            case "commit_skipped": {
                const reason = String(p.reason ?? "unknown");
                const cr = {
                    id: `skip-${ev.ts}`, sha: "", branch: String(p.branch ?? ""),
                    message: `(skipped — ${reason})`, files: [], ts: ev.ts,
                };
                setCurrentTurn((curr) => curr ? {
                    ...curr,
                    commits: [...curr.commits, cr],
                    blocks: [...curr.blocks, {
                            id: _newBlockId("commit", ev.ts, curr.blocks.length),
                            kind: "commit", commit: cr,
                        }],
                } : curr);
                return;
            }
            case "push_done": {
                if (sessionId)
                    sessionApi.git(sessionId).then(setGit).catch(() => { });
                return;
            }
            // --- Deploy lifecycle (in-place update OR new deploy) ---
            // The backend publishes deploy_complete / deploy_failed with the
            // canonical DeployedApp row in payload.result.app. Refresh the
            // pill list + the fresh_build flag so the UI re-renders without
            // a page reload. Without this handler, the deploy pill shows the
            // pre-deploy state until the user manually refreshes.
            case "deploy_complete": {
                if (sessionId) {
                    // Re-fetch both: deployedApps (so the pill shows the new
                    // public_url / last_redeploy_at / state) and detectedDist
                    // (so the fresh_build flag flips back to false and the
                    // "🔄 Update" button becomes "✓ Up to date").
                    sessionApi.deployedApps(sessionId).then(setDeployedApps).catch(() => { });
                    sessionApi.detectedDist(sessionId).then(setLastDetected).catch(() => { });
                }
                // Clear the per-slug progress chip — the deploy is done.
                const okApp = (p?.result?.app ?? {});
                if (okApp.slug) {
                    setDeployProgress((prev) => {
                        const { [okApp.slug]: _gone, ...rest } = prev;
                        return rest;
                    });
                    lastClickedUpdateSlugRef.current = null;
                }
                // Toast so the user knows it landed. payload.result.app.slug
                // is the canonical fresh row.
                if (okApp.slug) {
                    sessions.setToast?.({
                        message: `Deployed ${okApp.slug} — live at ${okApp.public_url || "(see pill)"}`,
                    });
                }
                return;
            }
            case "deploy_failed": {
                if (sessionId) {
                    // Refresh anyway: a failed in-place update may have left the
                    // row in state="error" that's worth surfacing in the pill.
                    sessionApi.deployedApps(sessionId).then(setDeployedApps).catch(() => { });
                }
                // Clear the in-flight progress chip and reset the slug ref.
                const failSlug = String(p?.result?.app?.slug ?? p?.slug ?? "");
                if (failSlug) {
                    setDeployProgress((prev) => {
                        const { [failSlug]: _gone, ...rest } = prev;
                        return rest;
                    });
                }
                lastClickedUpdateSlugRef.current = null;
                sessions.setToast?.({
                    message: `Deploy failed: ${String(p?.error ?? "unknown")}`,
                });
                return;
            }
            case "deploy_progress": {
                // Live in-flight step progress — drives the per-pill
                // "Step N/12 — Building…" chip so the user sees the
                // 10-30s fullstack deploy making real progress instead of
                // a frozen "Deploying…" pill. Backend publishes the
                // current phase string + step list at the TOP LEVEL of
                // the payload (see server/app.py deploy_progress publish
                // at the _set_step function). NOT under `result`.
                //
                // We need to know WHICH slug this progress is for. The
                // backend doesn't include slug in deploy_progress
                // (deploys are one-at-a-time per session), so we attribute
                // it to the slug the user most recently clicked Update on
                // for this session (the `lastClickedUpdateSlugRef` the
                // pill writes on click). If the backend ever runs parallel
                // deploys in one session, this will need a slug field on
                // the event itself.
                const phase = String(p?.phase ?? p?.step?.label ?? "Deploying");
                const steps = Array.isArray(p?.steps) ? p.steps : [];
                const steps_done = steps.filter((s) => s?.status === "done").length;
                const steps_total = steps.length || 0;
                // Resolve the slug: prefer the payload's slug if present,
                // else the last one the user clicked Update on for this
                // session.
                const slug = String(p?.slug ?? lastClickedUpdateSlugRef.current ?? "");
                if (slug) {
                    setDeployProgress((prev) => ({
                        ...prev,
                        [slug]: { phase, steps_done, steps_total },
                    }));
                }
                return;
            }
        }
    }
    // ---- Auto-scroll on new content (only while following) ----------------
    useEffect(() => {
        if (!chatAtBottom)
            return; // user has scrolled up; respect that
        const el = scrollRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
    }, [
        chatAtBottom,
        turns.length,
        currentTurn?.assistantText,
        currentTurn?.thinkingText,
        currentTurn?.tools.length,
    ]);
    // ---- Session totals — sum of per-turn metrics -------------------------
    const totals = useMemo(() => {
        return turns.reduce((acc, t) => {
            if (!t.summary)
                return acc;
            const s = t.summary;
            return {
                turns: acc.turns + 1,
                tools: acc.tools + s.tools_used,
                inputTokens: acc.inputTokens + s.input_tokens,
                outputTokens: acc.outputTokens + s.output_tokens,
                cacheReadTokens: acc.cacheReadTokens + s.cache_read_tokens,
                cacheWriteTokens: acc.cacheWriteTokens + s.cache_write_tokens,
                costUsd: acc.costUsd + s.cost_usd,
                // Per-component cost sub-totals. These default to 0 in the
                // accumulator when older turns pre-date the field — `??` handles
                // the optional `cost_*_usd` shape on TurnSummary.
                costCacheReadUsd: acc.costCacheReadUsd + (s.cost_cache_read_usd ?? 0),
                costInputUsd: acc.costInputUsd + (s.cost_input_usd ?? 0),
                costOutputUsd: acc.costOutputUsd + (s.cost_output_usd ?? 0),
                durationMs: acc.durationMs + s.duration_ms,
            };
        }, EMPTY_TOTALS);
    }, [turns]);
    // ---- Manual push -------------------------------------------------------
    const pushNow = async () => {
        if (!sessionId || pushing)
            return;
        setPushing(true);
        try {
            await sessionApi.push(sessionId);
            const g = await sessionApi.git(sessionId).catch(() => null);
            if (g)
                setGit(g);
        }
        finally {
            setPushing(false);
        }
    };
    // ---- Send a new prompt -------------------------------------------------
    // --- Prompt history (↑/↓ recalls past user messages, like a shell) ----
    // Stored in localStorage per session so refreshing doesn't lose it. Holds
    // up to the last 50 prompts; navigation index of -1 means "live input
    // (not browsing history)".
    const HISTORY_KEY = sessionId ? `chat_history:${sessionId}` : "";
    const [historyIdx, setHistoryIdx] = useState(-1);
    const [draftBeforeHistory, setDraftBeforeHistory] = useState("");
    const getHistory = () => {
        if (!HISTORY_KEY)
            return [];
        try {
            return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
        }
        catch {
            return [];
        }
    };
    const pushHistory = (text) => {
        if (!HISTORY_KEY || !text)
            return;
        const h = getHistory();
        // Dedupe consecutive identical entries.
        if (h[h.length - 1] === text)
            return;
        h.push(text);
        while (h.length > 50)
            h.shift();
        try {
            localStorage.setItem(HISTORY_KEY, JSON.stringify(h));
        }
        catch { }
    };
    // --- Help overlay toggle (/help) --------------------------------------
    const [helpOpen, setHelpOpen] = useState(false);
    // --- Slash-command autocomplete --------------------------------------
    // Picker is open whenever the input starts with "/" and there are matches.
    // `slashIdx` is the highlighted row inside the visible matches list — the
    // arrow-key handler moves it, Tab/Enter accept.
    const slashMatches = (() => {
        const t = input;
        if (!t.startsWith("/"))
            return [];
        const lower = t.toLowerCase();
        // Filter out admin-only commands for non-admins (currently: /debug).
        const visible = isAdmin
            ? SLASH_COMMANDS
            : SLASH_COMMANDS.filter((c) => c.cmd !== "/debug");
        const prefixMatches = visible.filter((c) => c.cmd.startsWith(lower));
        return prefixMatches.length
            ? prefixMatches
            // Fallback: substring match in case the user types "/sto" and "/stop"
            // is the only sensible completion (here it's equivalent to prefix, but
            // future commands like "/run-tests" benefit from this).
            : visible.filter((c) => c.cmd.includes(lower.slice(1)));
    })();
    const showSlashPicker = input.startsWith("/") && slashMatches.length > 0;
    const [slashIdx, setSlashIdx] = useState(0);
    // Reset highlight to the top whenever the visible match set changes.
    useEffect(() => { setSlashIdx(0); }, [input]);
    const acceptSlashCompletion = (i = slashIdx) => {
        const m = slashMatches[i];
        if (!m)
            return;
        // Trailing space makes it feel finished and lets the user keep typing
        // arguments if a future command takes them.
        setInput(m.cmd + " ");
    };
    // --- Slash-command dispatcher -----------------------------------------
    // Recognizes commands typed at the start of the input box. Anything else
    // falls through to the agent. Mirrors the feel of terminal REPLs / Claude
    // Code's slash commands — kept local to the UI so we don't have to route
    // through the agent for trivial actions.
    const runSlashCommand = async (raw) => {
        const cmd = raw.trim().toLowerCase();
        if (!cmd.startsWith("/"))
            return false;
        const [head] = cmd.slice(1).split(/\s+/, 1);
        switch (head) {
            case "help":
            case "?":
                setHelpOpen(true);
                return true;
            case "clear":
                // Local-only clear: hide past turns from view. Backend history is
                // preserved — refreshing the page restores everything.
                setTurns([]);
                setCurrentTurn(null);
                setPlan([]);
                return true;
            case "stop":
            case "cancel": {
                if (!sessionId)
                    return true;
                try {
                    await sessionApi.cancel(sessionId);
                }
                catch { }
                return true;
            }
            case "compact": {
                if (!sessionId)
                    return true;
                try {
                    const res = await sessionApi.compact(sessionId);
                    if (res.ok) {
                        alert(`Context compacted: ${res.before} → ${res.after} messages.\n\nThe agent's working memory is now smaller. The chat history above is unaffected — only the agent's internal context was trimmed.`);
                    }
                    else {
                        alert(`Couldn't compact: ${res.reason ?? "unknown reason"}`);
                    }
                }
                catch (e) {
                    alert(`Compact failed: ${e?.message ?? "request error"}`);
                }
                return true;
            }
            case "history": {
                // Drop the last 50 prompts into a transient "turn" so the user can
                // see them and re-run by clicking. Lightweight — just visual.
                const h = getHistory();
                if (h.length === 0) {
                    alert("No prompt history yet.");
                }
                else {
                    alert("Recent prompts (newest first):\n\n" +
                        h.slice().reverse().map((t, i) => `${i + 1}. ${t}`).join("\n"));
                }
                return true;
            }
            case "debug":
                if (!isAdmin)
                    return false;
                toggleDebug();
                return true;
            default:
                // Unknown slash: just send it through to the agent verbatim — the
                // model may know what to do (e.g. /undo, /retry might be wired
                // later). No silent swallow.
                return false;
        }
    };
    const send = async (e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text || !sessionId)
            return;
        // If a turn is mid-flight, the user hitting Enter should be
        // "stop what you're doing and answer THIS" — NOT a no-op that
        // leaves them feeling ignored. The old `if (sending) return`
        // guard made the input feel sticky when the agent was clearly
        // busy on something else; Claude Code never blocks the user
        // from interrupting. So:
        //
        //   1. Call sessionApi.cancel() to abort the in-flight turn
        //      server-side. The server's POST /messages handler now
        //      also cancels any in-flight turn before scheduling the
        //      new one, so this is belt-and-suspenders — if the cancel
        //      POST races the cancel-then-send server path, the
        //      server's own guard still serialises them correctly.
        //   2. Show "Stopping…" in the button so the user has visible
        //      feedback that their input was received.
        //   3. Wait for the WS turn_summary to arrive (which clears
        //      `sending` via the existing handler), with a bounded
        //      timeout in case the cancel finalisation hangs. Then
        //      submit the new message.
        //
        // We do NOT block on a fixed sleep — the WS handler is the
        // authoritative signal that the old turn has finalised. If it
        // arrives within 3s, great; if not, we send anyway because
        // the server's per-session serialisation (one turn at a time)
        // means the new POST will wait for the old finalisation on
        // its end. Better to send late than to leave the user with
        // a stuck "Stopping…" spinner.
        if (sending) {
            setInterrupting(true);
            try {
                // Fire cancel and wait for `sending` to flip false. The
                // WS turn_summary handler at line ~270 is what sets it
                // false — once the cancelled turn has fully finalised.
                sessionApi.cancel(sessionId).catch(() => { });
                const start = Date.now();
                while (sendingRef.current && Date.now() - start < 3000) {
                    await new Promise((r) => setTimeout(r, 50));
                }
            }
            finally {
                setInterrupting(false);
            }
        }
        // History bookkeeping — record every submitted line, reset nav cursor.
        pushHistory(text);
        setHistoryIdx(-1);
        setDraftBeforeHistory("");
        // Handle slash commands locally — don't hit the agent for /help, /clear, etc.
        // (Note: /stop and /cancel are still handled by runSlashCommand as a
        // fallback — but the user typing plain text + Enter mid-turn now goes
        // through the "stop and send" path above, which is the more common
        // Claude-Code-shaped gesture.)
        if (await runSlashCommand(text)) {
            setInput("");
            return;
        }
        setSending(true);
        // Optimistic open of a new turn so the user sees their prompt instantly.
        // The backend's user_message event echoes it back; handleEvent dedupes.
        setCurrentTurn(makeTurn(text, Date.now()));
        setInput("");
        try {
            await sessionApi.post(sessionId, text);
        }
        catch (e) {
            setCurrentTurn((curr) => curr ? {
                ...curr, error: e?.message ?? "send failed", isStreaming: false,
            } : curr);
        }
        finally {
            setSending(false);
        }
    };
    // Keyboard routing for the composer box. Priority: slash picker > history nav.
    // The picker is contextual — only active when the input starts with "/" and
    // there's at least one match — so plain typing is unaffected.
    //
    // Enter key semantics for the textarea (different from the old <input>):
    //   - Enter (no shift)  -> SUBMIT (matches the old <input> behavior so
    //                          muscle memory carries over; matches Claude.ai)
    //   - Shift+Enter       -> newline (the standard multi-line escape)
    // We handle this at the END of the function so slash-picker and history
    // overrides still win — those keys reach their branches first and `return`.
    const onInputKeyDown = (e) => {
        // ----- Slash picker active -----
        if (showSlashPicker) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSlashIdx((i) => (i + 1) % slashMatches.length);
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setSlashIdx((i) => (i - 1 + slashMatches.length) % slashMatches.length);
                return;
            }
            if (e.key === "Tab") {
                // Tab = accept highlighted completion, stay in input (don't submit).
                e.preventDefault();
                acceptSlashCompletion();
                return;
            }
            if (e.key === "Enter") {
                // If the user is hovering over a suggestion and the input doesn't
                // already exactly match a command, accept the suggestion first and
                // let the form submit handler (`send`) run the resulting command.
                const exact = SLASH_COMMANDS.find((c) => c.cmd === input.trim().toLowerCase());
                if (!exact) {
                    e.preventDefault();
                    acceptSlashCompletion();
                    // Submit immediately if the user pressed Enter to accept — feels
                    // more direct than requiring a second Enter for /clear, /stop, etc.
                    setTimeout(() => {
                        e.currentTarget?.form?.requestSubmit();
                    }, 0);
                    return;
                }
            }
            if (e.key === "Escape") {
                // Close the picker by clearing the slash (cheapest cancel).
                e.preventDefault();
                setInput("");
                return;
            }
            // Other keys fall through to default text editing.
            return;
        }
        // ----- History navigation (only when picker is closed) -----
        const h = getHistory();
        if (e.key === "ArrowUp" && h.length > 0) {
            e.preventDefault();
            if (historyIdx === -1)
                setDraftBeforeHistory(input);
            const next = historyIdx === -1 ? h.length - 1 : Math.max(0, historyIdx - 1);
            setHistoryIdx(next);
            setInput(h[next]);
        }
        else if (e.key === "ArrowDown" && historyIdx !== -1) {
            e.preventDefault();
            const next = historyIdx + 1;
            if (next >= h.length) {
                setHistoryIdx(-1);
                setInput(draftBeforeHistory);
            }
            else {
                setHistoryIdx(next);
                setInput(h[next]);
            }
        }
        else if (e.key === "Escape" && historyIdx !== -1) {
            e.preventDefault();
            setHistoryIdx(-1);
            setInput(draftBeforeHistory);
        }
        else if (e.key === "Enter" && !e.shiftKey) {
            // Enter (no shift) on the textarea = submit. Shift+Enter falls through
            // to the textarea's default behavior and inserts a newline. The form's
            // onSubmit handler (`send`) does the actual work. We preventDefault so
            // no extra newline sneaks in before the submit fires.
            e.preventDefault();
            e.currentTarget.form?.requestSubmit();
        }
        // Shift+Enter: no branch needed — textarea inserts "\n" by default.
    };
    // True when this is a brand-new session with no exchanges yet. Drives the
    // Claude.ai-style centered "Start a conversation" layout: the compose form
    // floats in the middle of the screen instead of being pinned at the bottom,
    // and the scroll/status chrome stay hidden until there's something to show.
    const isEmpty = turns.length === 0 && !currentTurn && !loadErr;
    // ============================================================================
    return (_jsxs("div", { className: "flex h-screen flex-col", children: [_jsxs("header", { className: "chrome-bar grid grid-cols-[auto_1fr_auto] items-center gap-2 px-3 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2 sm:gap-3 sm:px-4 sm:pt-3 sm:pb-3", children: [_jsxs("div", { className: `flex items-center gap-2 ${needsHamburgerSpace ? "pl-12" : ""}`, children: [_jsx(BranchBadge, { git: git }), git?.has_remote && git.ahead > 0 && (_jsx("button", { type: "button", onClick: pushNow, disabled: pushing, className: "pill pill-accent min-h-touch disabled:opacity-50", title: `Push ${git.ahead} commit(s) to origin`, children: pushing ? "Pushing…" : `↑ ${git.ahead}` }))] }), _jsxs("div", { className: "hidden min-w-0 items-center justify-center gap-2 sm:flex", children: [currentTurn && currentTurn.isStreaming && !currentTurn.error && (_jsx(NowChip, { turn: currentTurn })), _jsx(RunningTotals, { totals: totals })] }), _jsxs("div", { className: "flex items-center gap-2 justify-self-end", children: [_jsx(ContextChip, { used: contextUsed, threshold: contextThreshold, compacting: contextCompacting, compactCount: 
                                // Total compactions this session: count what's in the
                                // completed turns + the in-flight current turn + any
                                // orphans on the parent-level fallback list. All three
                                // can overlap, so dedupe by note id before counting.
                                (() => {
                                    const seen = new Set();
                                    const n = [];
                                    for (const t of turns)
                                        n.push(...(t.compactNotes ?? []));
                                    if (currentTurn)
                                        n.push(...(currentTurn.compactNotes ?? []));
                                    n.push(...contextCompactedNotes);
                                    for (const note of n)
                                        seen.add(note.id);
                                    return seen.size;
                                })(), lastCompactSavedTokens: 
                                // Tokens freed by the most recent compaction (just the
                                // last one's delta, not cumulative) — keeps the tooltip
                                // honest and short. Pull from whichever list has the
                                // newest note by ts.
                                (() => {
                                    const all = [];
                                    for (const t of turns)
                                        all.push(...(t.compactNotes ?? []));
                                    if (currentTurn)
                                        all.push(...(currentTurn.compactNotes ?? []));
                                    all.push(...contextCompactedNotes);
                                    if (all.length === 0)
                                        return 0;
                                    let newest = null;
                                    for (const note of all) {
                                        if (!newest || note.ts > newest.ts)
                                            newest = note;
                                    }
                                    return newest ? Math.max(0, newest.tokensBefore - newest.tokensAfter) : 0;
                                })(), cacheRead: contextCacheRead, cacheCreation: contextCacheCreation }), _jsx("button", { type: "button", onClick: toggleTheme, className: "btn-icon shrink-0", title: `Switch to ${themeEffective === "dark" ? "light" : "dark"} mode`, "aria-label": `Switch to ${themeEffective === "dark" ? "light" : "dark"} mode`, children: themeEffective === "dark" ? _jsx(SunIcon, { className: "h-4 w-4" }) : _jsx(MoonIcon, { className: "h-4 w-4" }) }), isAdmin && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: toggleDebug, title: debugOpen ? "Close debug stream" : "Open debug stream", "aria-label": "Toggle debug stream", className: `shrink-0 ${debugOpen ? "pill-accent" : ""} btn-icon sm:hidden`, children: "\u2318" }), _jsx("button", { type: "button", onClick: toggleDebug, title: "Toggle raw WebSocket event stream", className: `pill min-h-touch hidden sm:inline-flex ${debugOpen ? "pill-accent" : ""}`, children: "\u2318 debug" }), _jsx("button", { type: "button", onClick: () => setLlmTraceOpen((v) => !v), title: "Inspect every LLM call \u2014 request, response, token usage", "aria-label": "Toggle LLM trace", className: `pill min-h-touch ${llmTraceOpen ? "pill-accent" : ""}`, children: "\u2325 llm" })] }))] })] }), _jsx(PlanPanel, { items: plan }), _jsx(DeployStrip, { sessionId: sessionId ?? "", sessionName: turns[0]?.userPrompt ?? "app", apps: deployedApps, 
                // "+ Deploy new" is enabled only when there's an unbuilt
                // sub-app with a build (not a rebuild of an already-deployed
                // one — those go through the pill's "🔄 Update" instead).
                hasUnbuiltBuild: !!lastDetected?.has_unbuilt_build, 
                // Per-pill fresh-build lookup: each pill finds its own
                // candidate by deployed_slug and uses THAT candidate's
                // is_fresh (mtime > last_redeploy_at) — not the session-
                // wide freshest mtime — so only the genuinely rebuilt pill
                // shows Update in a multi-app session.
                lastDetected: lastDetected, 
                // Per-slug live deploy progress (set by deploy_progress WS
                // event). Pills use this to render a "Step 3/12 — Building…"
                // chip so the user sees the 10-30s fullstack deploy making
                // real progress instead of a frozen "Deploying…".
                deployProgress: deployProgress, onUpdateClicked: (slug) => {
                    // Mark which slug the user just clicked Update on so the
                    // next deploy_progress event (which doesn't carry a slug)
                    // can be attributed to it. Cleared on deploy_complete /
                    // deploy_failed.
                    lastClickedUpdateSlugRef.current = slug;
                }, 
                // Hoisted state so the build-ready banner can also open the
                // modal (single source of truth — both the strip button and
                // the banner button call setShowDeployModal(true)).
                showModal: showDeployModal, onShowModalChange: setShowDeployModal, onDeployed: (app) => {
                    setDeployedApps((prev) => [
                        app, ...prev.filter((a) => a.slug !== app.slug),
                    ]);
                    // After a successful deploy, the dist is no longer "fresh" —
                    // re-fetch detected-dist so the banner disappears and
                    // fresh_mtime is fresh again for the next build.
                    sessionApi.detectedDist(sessionId).then(setLastDetected).catch(() => { });
                }, onDeleted: (slug) => setDeployedApps((prev) => prev.filter((a) => a.slug !== slug)), onToggled: (slug, state) => setDeployedApps((prev) => prev.map((a) => (a.slug === slug ? { ...a, state } : a))) }), isAdmin && debugOpen && (_jsx(DebugStream, { events: debugEvents, onClear: () => setDebugEvents([]), onClose: toggleDebug })), isAdmin && llmTraceOpen && sessionId && (_jsx(LLMTracePanel, { sessionId: sessionId, onClose: () => setLlmTraceOpen(false) })), _jsxs("div", { className: `relative min-h-0 ${isEmpty ? "hidden" : "flex-1"}`, children: [_jsx("div", { ref: scrollRef, onScroll: onChatScroll, className: "h-full overflow-y-auto px-4", children: _jsxs("div", { className: "transcript mx-auto flex max-w-4xl flex-col pb-16", children: [loadErr && (_jsx("div", { className: "mt-4 rounded border border-danger/40 bg-danger/10 p-3 text-danger", children: loadErr })), turns.map((t, i) => (_jsx(TurnCard, { turn: t, index: i }, t.id))), (() => {
                                    const seen = new Set();
                                    for (const t of turns) {
                                        for (const n of t.compactNotes ?? [])
                                            seen.add(n.id);
                                    }
                                    if (currentTurn) {
                                        for (const n of currentTurn.compactNotes ?? [])
                                            seen.add(n.id);
                                    }
                                    const orphans = contextCompactedNotes.filter((n) => !seen.has(n.id));
                                    if (orphans.length === 0)
                                        return null;
                                    return (_jsx("div", { className: "space-y-2", children: orphans.map((n) => (_jsx(ContextCompactedNoteCard, { note: n }, n.id))) }));
                                })(), currentTurn && (_jsx(ActiveTurnCard, { turn: currentTurn, index: turns.length })), turns.length === 0 && !currentTurn && !loadErr && (_jsx(EmptyState, {}))] }) }), !chatAtBottom && (_jsx("button", { type: "button", onClick: jumpChatToBottom, className: "absolute bottom-3 right-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-elevated/95 text-base text-text shadow-lift backdrop-blur-md hover:border-accent/60 hover:bg-accent/15 hover:text-accent", title: "Jump to latest", "aria-label": "Jump to latest activity", children: "\u2193" }))] }), isEmpty && (_jsxs("div", { className: "mt-auto px-4 pt-8 text-center", children: [_jsx("div", { "aria-hidden": true, className: "mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-gradient shadow-lift", children: _jsx("span", { className: "text-2xl font-bold text-white", children: "\u2318" }) }), _jsx("h1", { className: "font-serif text-3xl font-semibold tracking-tight text-text sm:text-4xl", children: "Start a conversation" }), _jsx("p", { className: "mx-auto mt-2.5 max-w-md text-sm text-muted", children: "Type a request below. The agent will reply, call tools, edit files, and track tokens \u2014 everything visible as it happens." })] })), !isEmpty && _jsx(ChatStatusBar, { currentTurn: currentTurn }), _jsxs("form", { onSubmit: send, className: isEmpty
                    ? "relative mb-auto w-full px-3 pb-8 pt-4"
                    : "chrome-bar-bottom relative px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]", children: [showSlashPicker && (_jsx("div", { className: "pointer-events-none absolute bottom-full left-0 right-0 px-3 pb-2", children: _jsxs("div", { className: "pointer-events-auto mx-auto max-w-4xl overflow-hidden rounded-lg border border-border bg-surface/95 shadow-lift backdrop-blur-md", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-border/60 bg-elevated/60 px-3 py-1.5", children: [_jsx("span", { className: "text-[10px] font-bold uppercase tracking-[0.16em] text-accent", children: "Commands" }), _jsxs("span", { className: "font-sans text-[10px] text-subtle", children: [_jsx("kbd", { className: "rounded border border-border bg-bg/60 px-1 font-mono text-[10px]", children: "\u2191\u2193" }), " move \u00B7", " ", _jsx("kbd", { className: "rounded border border-border bg-bg/60 px-1 font-mono text-[10px]", children: "Tab" }), " accept \u00B7", " ", _jsx("kbd", { className: "rounded border border-border bg-bg/60 px-1 font-mono text-[10px]", children: "\u21B5" }), " run \u00B7", " ", _jsx("kbd", { className: "rounded border border-border bg-bg/60 px-1 font-mono text-[10px]", children: "Esc" }), " cancel"] })] }), _jsx("ul", { className: "max-h-72 overflow-y-auto py-1", children: slashMatches.map((m, i) => (_jsx("li", { children: _jsxs("button", { type: "button", onMouseDown: (e) => {
                                                // mouseDown (not click) so we accept before the input
                                                // loses focus and the picker unmounts.
                                                e.preventDefault();
                                                acceptSlashCompletion(i);
                                            }, onMouseEnter: () => setSlashIdx(i), className: "flex w-full items-baseline gap-3 px-3 py-2 text-left " +
                                                (i === slashIdx ? "bg-accent/10" : "hover:bg-elevated/50"), children: [_jsx("span", { className: "shrink-0 rounded border px-1.5 py-0.5 font-mono text-tx-xs " +
                                                        (i === slashIdx
                                                            ? "border-accent/60 bg-accent/15 text-accent"
                                                            : "border-border bg-bg/60 text-text"), children: m.cmd }), _jsx("span", { className: "truncate text-tx-sm text-muted", children: m.desc })] }) }, m.cmd))) })] }) })), _jsxs("div", { className: "mx-auto flex max-w-4xl flex-col gap-1", children: [_jsxs("div", { className: "flex items-center gap-1.5 px-1 font-sans text-[10px] text-subtle", children: [_jsx("kbd", { className: "rounded border border-border bg-elevated/60 px-1 py-px font-mono text-[10px]", children: "\u2191" }), _jsx("span", { children: "prev" }), _jsx("kbd", { className: "rounded border border-border bg-elevated/60 px-1 py-px font-mono text-[10px]", children: "\u2193" }), _jsx("span", { children: "next" }), _jsx("span", { className: "text-border", children: "\u00B7" }), _jsx("button", { type: "button", onClick: () => setHelpOpen(true), className: "hover:text-accent", title: "Show all slash commands", children: "/help" }), _jsx("span", { className: "text-border", children: "\u00B7" }), _jsx("button", { type: "button", onClick: () => setInput("/clear"), className: "hover:text-accent", children: "/clear" }), _jsx("span", { className: "text-border", children: "\u00B7" }), _jsx("button", { type: "button", onClick: () => setInput("/stop"), className: "hover:text-accent", children: "/stop" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("textarea", { ref: composerRef, value: input, onChange: (e) => setInput(e.target.value), onKeyDown: onInputKeyDown, 
                                        // Placeholder flips mid-turn to teach the new gesture:
                                        // "press Enter to stop and ask" — Claude-Code style.
                                        // Without this hint, users don't know that Enter while
                                        // a turn is in flight will interrupt the old turn AND
                                        // submit the new question (the old `sending` guard
                                        // made Enter a silent no-op).
                                        placeholder: currentTurn?.isStreaming
                                            ? "Press Enter to stop current task and ask this…"
                                            : "Type a message, or / for commands…", rows: 1, 
                                        // Auto-grow driven by the useLayoutEffect above (see
                                        // `composerRef` + `MAX_COMPOSER_HEIGHT`). Classes: `field`
                                        // gives the base look, `min-h-touch` keeps the collapsed
                                        // height tappable, `flex-1` fills the row, `resize-none`
                                        // disables the bottom-right drag handle (the user can't
                                        // override our cap), `leading-snug` + `py-2` give the
                                        // 24-px line-height used to compute MAX_COMPOSER_HEIGHT,
                                        // and `overflow-hidden` is set dynamically by the effect.
                                        className: "field min-h-touch flex-1 resize-none overflow-hidden text-base leading-snug py-2 md:text-sm", autoCapitalize: "sentences", autoCorrect: "on", 
                                        // `enterKeyHint` is iOS-only — on mobile keyboards it
                                        // shows "Return" / "Send" / etc. on the return key.
                                        enterKeyHint: "send" }), currentTurn?.isStreaming && !currentTurn.error ? (
                                    // Mid-turn. The button is Send (not Stop) so the
                                    // single-click action is "stop and send my new
                                    // question" — which is what the user is trying to
                                    // do when they type + Enter mid-task. If the input
                                    // is empty, the button is a plain Stop (cancel only).
                                    _jsx("button", { type: input.trim() ? "submit" : "button", onClick: async () => {
                                            if (!sessionId)
                                                return;
                                            // Only stop-don't-send if the input is empty.
                                            // If the input has text, let the form submit
                                            // handler run (which will stop + send).
                                            if (!input.trim()) {
                                                try {
                                                    await sessionApi.cancel(sessionId);
                                                }
                                                catch { }
                                            }
                                        }, 
                                        // Visual cue: orange-tinted mid-turn (not the
                                        // danger-red pure Stop, not the accent Send) so
                                        // the user reads it as "send, but it'll interrupt".
                                        // Plus a small spinner dot while `interrupting` is
                                        // true so the user sees their click landed.
                                        className: "min-h-touch min-w-touch rounded-lg border px-4 py-2 text-sm font-semibold transition " +
                                            (interrupting
                                                ? "border-warn/60 bg-warn/15 text-warn"
                                                : input.trim()
                                                    ? "border-warn/40 bg-warn/10 text-warn hover:bg-warn/15"
                                                    : "border-danger/40 bg-danger/10 text-danger hover:bg-danger/15"), title: input.trim()
                                            ? "Stop the current task and send this question"
                                            : "Cancel the in-flight turn (/stop)", children: interrupting ? (_jsxs("span", { className: "inline-flex items-center gap-1.5", children: [_jsx("span", { className: "inline-block size-3 animate-spin rounded-full border-2 border-warn border-t-transparent" }), "Stopping\u2026"] })) : input.trim() ? ("Interrupt & Send") : ("■ Stop") })) : (_jsx("button", { type: "submit", disabled: !input.trim(), className: "btn-primary min-h-touch min-w-touch", children: sending ? "…" : "Send" }))] })] })] }), helpOpen && (_jsx(HelpOverlay, { isAdmin: isAdmin, onClose: () => setHelpOpen(false) }))] }));
}
// ============================================================================
// Helpers + small components
// ============================================================================
function makeTurn(userPrompt, ts) {
    return {
        id: `turn-${ts}-${Math.random().toString(36).slice(2, 6)}`,
        userPrompt, startedAt: ts,
        assistantText: "", thinkingText: "", isStreaming: true,
        tools: [], fileChanges: [], agents: {}, commits: [],
        compactNotes: [],
        blocks: [],
        liveInputTokens: 0, liveOutputTokens: 0,
        summary: null, error: null,
    };
}
// Helpers for maintaining the chronological block timeline. Consecutive text
// (or consecutive thinking) chunks merge into the same block — anything else
// closes the streak.
function _newBlockId(kind, ts, n) {
    return `${kind}-${ts}-${n}-${Math.random().toString(36).slice(2, 6)}`;
}
function _appendTextChunk(blocks, kind, text, ts) {
    if (!text)
        return blocks;
    const last = blocks[blocks.length - 1];
    if (last && last.kind === kind) {
        const next = [...blocks];
        next[next.length - 1] = { ...last, text: last.text + text };
        return next;
    }
    return [...blocks, {
            id: _newBlockId(kind, ts, blocks.length),
            kind, text, startedAt: ts,
        }];
}
function _replaceLastTextBlock(blocks, canonical, ts) {
    // Replace the most-recent text block with the canonical tag-stripped text
    // (the assistant_text(done=true) flush). If no text block exists yet (rare
    // edge case where the model only emitted a tool call), push one.
    for (let i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i].kind === "text") {
            const next = [...blocks];
            next[i] = { ...next[i], text: canonical };
            return next;
        }
    }
    return [...blocks, {
            id: _newBlockId("text", ts, blocks.length),
            kind: "text", text: canonical, startedAt: ts,
        }];
}
// Replay an event log into turns + final plan. Pure function, used both on
// mount (to restore state) and is the model the live handler emulates.
function rebuildTranscript(events) {
    const completed = [];
    let curr = null;
    let plan = [];
    for (const ev of events) {
        const p = ev.payload;
        switch (ev.kind) {
            case "user_message":
                if (curr)
                    completed.push({ ...curr, isStreaming: false });
                curr = makeTurn(String(p.text ?? ""), ev.ts);
                break;
            case "assistant_text":
                if (!curr)
                    break;
                if (p.done) {
                    const t = String(p.text ?? "");
                    if (t) {
                        curr.assistantText = t;
                        curr.blocks = _replaceLastTextBlock(curr.blocks, t, ev.ts);
                    }
                    curr.isStreaming = false;
                }
                else {
                    const t = String(p.text ?? "");
                    curr.assistantText += t;
                    curr.blocks = _appendTextChunk(curr.blocks, "text", t, ev.ts);
                }
                break;
            case "thinking_text": {
                if (!curr)
                    break;
                const t = String(p.text ?? "");
                curr.thinkingText += t;
                curr.blocks = _appendTextChunk(curr.blocks, "thinking", t, ev.ts);
                break;
            }
            case "turn_summary":
                if (!curr)
                    break;
                curr.summary = {
                    tools_used: Number(p.tools_used ?? 0),
                    duration_ms: Number(p.duration_ms ?? 0),
                    input_tokens: Number(p.input_tokens ?? 0),
                    output_tokens: Number(p.output_tokens ?? 0),
                    cache_read_tokens: Number(p.cache_read_tokens ?? 0),
                    cache_write_tokens: Number(p.cache_write_tokens ?? 0),
                    cost_usd: Number(p.cost_usd ?? 0),
                };
                curr.isStreaming = false;
                completed.push(curr);
                curr = null;
                break;
            case "tool_start": {
                if (!curr)
                    break;
                const aid = typeof p.agent_id === "string" ? p.agent_id : "";
                const tool = {
                    id: `${ev.ts}-${p.tool}-${curr.tools.length}-${curr.blocks.length}`,
                    tool: String(p.tool ?? "?"),
                    target: typeof p.target === "string" ? p.target : undefined,
                    status: "running", startedAt: ev.ts,
                };
                if (aid && curr.agents[aid]) {
                    curr.agents[aid].tools.push(tool);
                }
                else {
                    curr.tools.push(tool);
                    curr.blocks.push({
                        id: _newBlockId("tool", ev.ts, curr.blocks.length),
                        kind: "tool", toolId: tool.id, ts: ev.ts,
                    });
                }
                break;
            }
            case "tool_done": {
                if (!curr)
                    break;
                const aid = typeof p.agent_id === "string" ? p.agent_id : "";
                const list = (aid && curr.agents[aid]) ? curr.agents[aid].tools : curr.tools;
                const _bashOutput = parseBashOutputMarker(typeof p.preview === "string" ? p.preview : undefined);
                for (let i = list.length - 1; i >= 0; i--) {
                    if (list[i].tool === p.tool && list[i].status === "running") {
                        list[i] = {
                            ...list[i],
                            status: p.error ? "error" : "done",
                            preview: typeof p.preview === "string" ? p.preview : undefined,
                            previewTruncated: !!p.preview_truncated,
                            bashOutput: _bashOutput ?? list[i].bashOutput,
                            endedAt: ev.ts,
                        };
                        break;
                    }
                }
                break;
            }
            case "file_changed": {
                if (!curr)
                    break;
                const fc = {
                    id: `${p.path}-${ev.ts}`,
                    path: String(p.path ?? ""),
                    kind: p.kind === "create" ? "create" : "edit",
                    diff: String(p.diff ?? ""),
                    bytes: Number(p.bytes ?? 0),
                    ts: ev.ts,
                };
                curr.fileChanges.push(fc);
                curr.blocks.push({
                    id: _newBlockId("file", ev.ts, curr.blocks.length),
                    kind: "file", file: fc,
                });
                break;
            }
            case "agent_spawn": {
                if (!curr)
                    break;
                const aid = String(p.agent_id ?? "");
                curr.agents[aid] = {
                    agent_id: aid,
                    description: String(p.description ?? ""),
                    subagent_type: String(p.subagent_type ?? "general-purpose"),
                    name: String(p.name ?? ""), model: String(p.model ?? ""),
                    status: "running", output_file: "", error: "",
                    spawned_at: ev.ts, updated_at: ev.ts,
                    tools: [], liveInputTokens: 0, liveOutputTokens: 0, llmCalls: [],
                };
                curr.blocks.push({
                    id: _newBlockId("agent", ev.ts, curr.blocks.length),
                    kind: "agent", agentId: aid, ts: ev.ts,
                });
                break;
            }
            case "token_update": {
                if (!curr)
                    break;
                const aid = typeof p.agent_id === "string" ? p.agent_id : "";
                const inD = Number(p.input_delta ?? 0);
                const outD = Number(p.output_delta ?? 0);
                const cR = Number(p.cache_read_delta ?? 0);
                const cW = Number(p.cache_creation_delta ?? 0);
                if (inD === 0 && outD === 0 && cR === 0 && cW === 0)
                    break;
                if (aid && curr.agents[aid]) {
                    curr.agents[aid].liveInputTokens += inD;
                    curr.agents[aid].liveOutputTokens += outD;
                    curr.agents[aid].llmCalls.push({
                        ts: ev.ts, inputTokens: inD, outputTokens: outD,
                        cacheReadTokens: cR, cacheCreationTokens: cW,
                    });
                }
                else {
                    curr.liveInputTokens += inD;
                    curr.liveOutputTokens += outD;
                    curr.blocks.push({
                        id: _newBlockId("llm_call", ev.ts, curr.blocks.length),
                        kind: "llm_call", ts: ev.ts,
                        inputTokens: inD, outputTokens: outD,
                        cacheReadTokens: cR, cacheCreationTokens: cW,
                    });
                }
                break;
            }
            case "agent_status_update":
                if (!curr)
                    break;
                {
                    const id = String(p.agent_id ?? "");
                    const existing = curr.agents[id];
                    if (existing) {
                        curr.agents[id] = {
                            ...existing,
                            status: String(p.status ?? existing.status),
                            output_file: String(p.output_file ?? existing.output_file),
                            error: String(p.error ?? ""),
                            updated_at: ev.ts,
                        };
                    }
                }
                break;
            case "commit_made": {
                if (!curr)
                    break;
                const cr = {
                    id: `${p.sha}-${ev.ts}`,
                    sha: String(p.sha ?? ""), branch: String(p.branch ?? ""),
                    message: String(p.message ?? ""),
                    files: Array.isArray(p.files) ? p.files : [],
                    ts: ev.ts,
                };
                curr.commits.push(cr);
                curr.blocks.push({
                    id: _newBlockId("commit", ev.ts, curr.blocks.length),
                    kind: "commit", commit: cr,
                });
                break;
            }
            case "commit_skipped": {
                if (!curr)
                    break;
                const cr = {
                    id: `skip-${ev.ts}`, sha: "", branch: String(p.branch ?? ""),
                    message: `(skipped — ${p.reason ?? "unknown"})`, files: [], ts: ev.ts,
                };
                curr.commits.push(cr);
                curr.blocks.push({
                    id: _newBlockId("commit", ev.ts, curr.blocks.length),
                    kind: "commit", commit: cr,
                });
                break;
            }
            case "error":
                if (curr) {
                    curr.error = String(p.message ?? "unknown error");
                    curr.isStreaming = false;
                }
                break;
            case "todo_update":
                plan = Array.isArray(p.items) ? p.items : [];
                break;
            case "context_compacted": {
                // Attach the compact breadcrumb to the LAST COMPLETED TURN,
                // not the in-progress `curr`. The backend fires this event
                // PRE-LLM in `node_agent`; by the time the event reaches the
                // WS replay, the *new* turn has already been opened, so
                // `curr` = the new turn. But the summarised history belongs
                // to the PRIOR turn's response — the one whose accumulated
                // tool calls + reasoning just pushed context over the
                // threshold. The user wants to see the card at the bottom
                // of that prior turn, not at the top of the new one. See
                // the live WS handler (line 533) for the matching change +
                // a longer rationale on the semantic split.
                const removed = Number(p.removed ?? 0);
                if (removed <= 0)
                    break;
                const note = {
                    id: `compact-${ev.ts}-${removed}-${Number(p.kept ?? 0)}`,
                    ts: ev.ts,
                    removed,
                    kept: Number(p.kept ?? 0),
                    tokensBefore: Number(p.tokens_before ?? 0),
                    tokensAfter: Number(p.tokens_after ?? 0),
                    summaryPreview: String(p.summary_preview ?? ""),
                    threshold: Number(p.threshold ?? 0),
                };
                if (completed.length > 0) {
                    const last = completed[completed.length - 1];
                    completed[completed.length - 1] = {
                        ...last,
                        compactNotes: [...(last.compactNotes ?? []), note],
                    };
                }
                else if (curr) {
                    // Edge case: the very first user prompt of a fresh
                    // session was already at threshold. No completed turns
                    // to attach to. Fall back to the active turn so the
                    // card has SOME home.
                    curr.compactNotes = [...(curr.compactNotes ?? []), note];
                }
                break;
            }
        }
    }
    // If the log ended mid-turn, return the in-progress turn as `currentTurn`
    // (NOT shoved into the completed turns array). This is the critical fix
    // for joining a session that's actively streaming: without it, refreshing
    // the page or switching to a session mid-turn left curr=null, so every
    // subsequent live WS event handler (`setCurrentTurn(c => c ? {...} : c)`)
    // silently no-op'd — the Stop button stayed hidden and streaming text
    // never appeared. Keeping isStreaming=true here is what unlocks the Stop
    // button rendering condition and the live spinner.
    return { turns: completed, currentTurn: curr, plan };
}
// ============================================================================
// ContextChip — "X% used" pill in the chat header. The % is the TOTAL
// prompt the LLM received this turn divided by the auto-compact
// threshold. 100% = next LLM call will trigger auto-compact (the pre-LLM
// hook summarises old turns before model.invoke, then the chip drops to
// the post-compact total). Color tiers: <60 calm, 60-89 warn, 90+ danger,
// compacting shows a pulsing accent dot. Click the chip for the breakdown
// (new vs cache hits, cumulative compaction count, last save).
// ============================================================================
function ContextChip({ used, threshold, compacting, compactCount, lastCompactSavedTokens, cacheRead, cacheCreation, }) {
    const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k` : String(n);
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);
    const [popPos, setPopPos] = useState(null);
    // Click-outside + Esc-to-close + re-anchor on scroll/resize while open.
    useEffect(() => {
        if (!open)
            return;
        const computePos = () => {
            if (!buttonRef.current)
                return;
            const r = buttonRef.current.getBoundingClientRect();
            setPopPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
        };
        const onDoc = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => { if (e.key === "Escape")
            setOpen(false); };
        computePos();
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("touchstart", onDoc);
        document.addEventListener("keydown", onKey);
        window.addEventListener("scroll", computePos, true);
        window.addEventListener("resize", computePos);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("touchstart", onDoc);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("scroll", computePos, true);
            window.removeEventListener("resize", computePos);
        };
    }, [open]);
    // Clamp to 100% — the chip is a "till the next auto-compact" gauge,
    // not a "how many times over" counter.
    const pctUsed = threshold > 0
        ? Math.max(0, Math.min(100, Math.round((used / threshold) * 100)))
        : 0;
    // Color tier: compacting > danger > warn > calm.
    const tier = compacting ? "accent"
        : pctUsed >= 90 ? "danger"
            : pctUsed >= 60 ? "warn"
                : "calm";
    const tierCls = {
        calm: { dot: "bg-accent/40", text: "text-text", border: "border-border/60 bg-elevated/60" },
        warn: { dot: "bg-warn/80", text: "text-warn", border: "border-warn/40 bg-warn/[0.05]" },
        danger: { dot: "bg-danger/80", text: "text-danger", border: "border-danger/40 bg-danger/[0.06]" },
        accent: { dot: "bg-accent animate-pulse-soft", text: "text-accent", border: "border-accent/40 bg-accent/[0.06]" },
    }[tier];
    const label = compacting ? "Compacting…" : `${pctUsed}% used`;
    // "New" is what's left of the total after subtracting cache fields.
    // Avoids re-deriving `used - cacheRead - cacheCreation` inline twice.
    const newTokens = Math.max(0, used - cacheRead - cacheCreation);
    const hitRate = used > 0 ? Math.round((cacheRead / used) * 100) : 0;
    return (_jsxs("div", { ref: wrapperRef, className: "relative inline-flex shrink-0", children: [_jsxs("button", { ref: buttonRef, type: "button", onClick: () => setOpen((o) => !o), "aria-expanded": open, "aria-haspopup": "dialog", className: `inline-flex shrink-0 items-center gap-1.5 rounded-md border ${tierCls.border} px-2 py-0.5 font-sans backdrop-blur-sm transition hover:border-text/30`, title: "Click for context details", children: [_jsx("span", { className: `h-1.5 w-1.5 shrink-0 rounded-full ${tierCls.dot}` }), _jsx("span", { className: `text-tx-xs font-medium ${tierCls.text}`, children: label }), compactCount > 0 && !compacting && (_jsxs("span", { className: "text-tx-xs text-text/50", children: ["\u00B7", compactCount, "\u00D7"] }))] }), open && createPortal(_jsxs("div", { role: "dialog", "aria-label": "Context usage details", style: popPos
                    ? { position: "fixed", top: popPos.top, right: popPos.right }
                    : { position: "fixed", top: -9999, right: -9999, visibility: "hidden" }, className: "z-[60] w-72 rounded-md border border-border bg-elevated/95 p-3 text-tx-xs text-text shadow-lift backdrop-blur-md", 
                // Stop mousedown/touchstart from reaching the document
                // click-outside listener (otherwise text selection inside
                // the popover would dismiss it).
                onMouseDown: (e) => e.stopPropagation(), onTouchStart: (e) => e.stopPropagation(), children: [compacting ? (_jsxs("p", { className: "text-text/80", children: [_jsx("span", { className: "font-medium text-accent", children: "Compacting context" }), " — summarising older turns to keep the session running."] })) : (_jsxs(_Fragment, { children: [_jsxs("p", { className: "mb-1.5 text-text/90", children: [_jsx("span", { className: "font-medium", children: fmt(used) }), " total prompt this turn.", " ", "Auto-compact fires at ", _jsx("span", { className: "font-medium", children: fmt(threshold) }), " (resets to 0% on the next call)."] }), (cacheRead > 0 || cacheCreation > 0) && (_jsxs("p", { className: "mb-1.5 text-text/70", children: ["Breakdown: ", _jsx("span", { className: "font-medium", children: fmt(newTokens) }), " new", cacheRead > 0 && _jsxs(_Fragment, { children: [" \u00B7 ", _jsx("span", { className: "font-medium", children: fmt(cacheRead) }), " cache hits"] }), cacheCreation > 0 && _jsxs(_Fragment, { children: [" \u00B7 ", _jsx("span", { className: "font-medium", children: fmt(cacheCreation) }), " cache writes"] }), hitRate > 0 && _jsxs(_Fragment, { children: [" \u00B7 ", _jsxs("span", { className: "font-medium", children: [hitRate, "%"] }), " hit rate"] })] }))] })), compactCount > 0 && (_jsxs("p", { className: "mt-1.5 border-t border-border/40 pt-1.5 text-text/70", children: [_jsx("span", { className: "font-medium", children: compactCount }), " ", "compaction", compactCount === 1 ? "" : "s", " this session", lastCompactSavedTokens > 0 && (_jsxs(_Fragment, { children: [" \u00B7 last one freed ", _jsx("span", { className: "font-medium", children: fmt(lastCompactSavedTokens) }), " tokens"] }))] }))] }), document.body)] }));
}
// ============================================================================
// ContextCompactedNoteCard — collapsible system message rendered in the
// chat transcript each time auto-compaction fires (one per
// `context_compacted` WS event). Default state is folded — the user
// expands to read the summary preview. Keeps the chat clean while still
// giving a visible breadcrumb of WHAT got summarised WHEN.
// ============================================================================
function ContextCompactedNoteCard({ note }) {
    const [open, setOpen] = useState(false);
    const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k` : String(n);
    const freed = Math.max(0, note.tokensBefore - note.tokensAfter);
    return (_jsx("div", { className: "mt-2 flex justify-start", children: _jsxs("div", { className: "max-w-[92%] rounded-md border border-accent/30 bg-accent/[0.04] text-text", children: [_jsxs("button", { type: "button", onClick: () => setOpen((o) => !o), className: "flex w-full items-center gap-2 px-3 py-1.5 text-left text-tx-xs", title: open ? "Hide auto-compact details" : "Show what was summarised", children: [_jsx("span", { className: "text-accent", children: "\uD83D\uDCE6" }), _jsx("span", { className: "font-medium text-text/90", children: "Auto-compacted context" }), _jsxs("span", { className: "text-text/60", children: ["\u00B7 summarised ", note.removed, " older message", note.removed === 1 ? "" : "s", " (", fmt(freed), " tokens freed) \u00B7 kept ", note.kept, " recent"] }), _jsx("span", { className: "ml-auto text-text/50", children: open ? "▾" : "▸" })] }), open && (_jsxs("div", { className: "border-t border-accent/20 px-3 py-2 text-tx-xs text-text/80", children: [_jsxs("p", { className: "mb-1.5 text-text/60", children: ["Threshold: ", fmt(note.threshold || 50_000), " tokens. Before: ", fmt(note.tokensBefore), ". After: ", fmt(note.tokensAfter), "."] }), note.summaryPreview ? (_jsxs("pre", { className: "whitespace-pre-wrap rounded bg-elevated/60 p-2 font-sans text-text/80", children: [note.summaryPreview, note.summaryPreview.length >= 280 ? "…" : ""] })) : (_jsx("p", { className: "text-text/50", children: "No preview captured for this compaction." })), _jsx("p", { className: "mt-1.5 text-text/50", children: "The full summary is in the conversation as a hidden HumanMessage \u2014 the agent can re-read it any time." })] }))] }) }));
}
// ============================================================================
// ChatStatusBar — sticky LIVE activity strip pinned above the compose divider.
//
// Only renders while a turn is actually running. Completed turns keep their
// own per-turn stats card inside the TurnCard (so history is inspectable on
// scroll). Centered to the same max-width as the chat content so it visually
// lines up with the conversation above instead of running edge-to-edge.
// ============================================================================
function ChatStatusBar({ currentTurn, }) {
    const [now, setNow] = useState(() => Date.now());
    // Admin "show only new tokens" mode — same toggle the per-turn footer and
    // the per-call LLM markers honour. When on, the live In figure collapses to
    // just the new (uncached) tokens and the "(X cached)" parenthetical is hidden,
    // so the running strip matches what the completed turn footer will show.
    const { tokensShowNewOnly } = useAppSettings();
    useEffect(() => {
        if (!currentTurn?.isStreaming)
            return;
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, [currentTurn?.isStreaming]);
    if (!currentTurn || !currentTurn.isStreaming || currentTurn.error)
        return null;
    const totalIn = currentTurn.liveInputTokens;
    const totalOut = currentTurn.liveOutputTokens;
    const runningTools = currentTurn.tools.filter((t) => t.status === "running").length;
    const runningAgents = Object.values(currentTurn.agents).filter((a) => a.status === "running").length;
    // Live cache hits — sum cacheReadTokens across every orchestrator-level
    // llm_call block this turn has produced. The blocks carry per-call cache
    // deltas; the running `liveInputTokens` does not (it includes both
    // cache_read and uncached alike).
    const liveCacheRead = currentTurn.blocks.reduce((acc, b) => {
        return b.kind === "llm_call" ? acc + (b.cacheReadTokens ?? 0) : acc;
    }, 0);
    const liveNew = Math.max(0, totalIn - liveCacheRead);
    // Live cost is intentionally NOT shown here — the server's authoritative
    // per-component cost lands in turn_summary, and pricing client-side risks
    // drift from the server's MODEL_PRICING table. The per-turn footer and
    // session chip show the real number.
    return (_jsx("div", { className: "bg-bg", children: _jsx("div", { className: "mx-auto max-w-4xl px-4 py-2", children: _jsxs("div", { className: "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-accent/25 bg-accent/10 px-3 py-1.5 font-sans text-tx-xs", children: [_jsxs("span", { className: "inline-flex items-center gap-1.5", children: [_jsx("span", { className: "stream-dot" }), _jsx("span", { className: "text-[10px] font-bold uppercase tracking-[0.18em] text-accent", children: "Live" })] }), _jsx(Sep, {}), _jsx(StatItem, { label: "Elapsed", value: formatDuration(now - currentTurn.startedAt) }), _jsx(Sep, {}), _jsx(StatItem, { label: "Tools", value: `${currentTurn.tools.length}${runningTools ? ` (${runningTools} run)` : ""}`, valueClass: runningTools ? "text-warn" : "text-text" }), Object.keys(currentTurn.agents).length > 0 && (_jsxs(_Fragment, { children: [_jsx(Sep, {}), _jsx(StatItem, { label: "Agents", value: `${Object.keys(currentTurn.agents).length}${runningAgents ? ` (${runningAgents} run)` : ""}`, valueClass: runningAgents ? "text-warn" : "text-text" })] })), (totalIn > 0 || totalOut > 0) && (_jsxs(_Fragment, { children: [_jsx(Sep, {}), _jsx(StatItem, { label: "In", value: tokensShowNewOnly
                                    ? formatTokens(liveNew)
                                    : liveCacheRead > 0
                                        ? `${formatTokens(totalIn)} (${formatTokens(liveCacheRead)} cached)`
                                        : formatTokens(totalIn), valueClass: "text-accent", title: tokensShowNewOnly
                                    ? `${liveNew.toLocaleString()} new (uncached) input tokens`
                                    : liveCacheRead > 0
                                        ? `${totalIn.toLocaleString()} in · ${liveCacheRead.toLocaleString()} cache hits · ${liveNew.toLocaleString()} new`
                                        : `${totalIn.toLocaleString()} in` }), _jsx(StatItem, { label: "Out", value: formatTokens(totalOut), valueClass: "text-accent-2" })] }))] }) }) }));
}
function StatItem({ label, value, valueClass = "text-text", title, }) {
    return (_jsxs("span", { className: "inline-flex items-baseline gap-1", title: title, children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-[0.14em] text-subtle", children: label }), _jsx("span", { className: `font-mono text-tx-sm ${valueClass}`, children: value })] }));
}
function Sep() {
    return _jsx("span", { className: "text-subtle", children: "\u00B7" });
}
// Parse the bash-output status marker the bash tool always appends to
// its preview. Two formats, both set in tools/wrappers.py: see
// _smart_truncate_bash_output. The marker is always present on bash
// calls so the chat UI can show a single status line for every
// invocation, not just the truncated ones.
//
//   passed_through:  [bash-output: total=5234, cap=10000, status=passed_through]
//   truncated:       [bash-output: total=25939, cap=10000, status=truncated,
//                                 kept_first=2800, kept_last=6200, dropped=16939,
//                                 verdict=FAILURE, spill=/tmp/ojas-bash/...log]
//
// Returns null when the preview doesn't contain a marker (the tool
// isn't bash, or the preview hasn't arrived yet).
function parseBashOutputMarker(preview) {
    if (!preview)
        return null;
    const m = preview.match(/\[bash-output:\s+total=(\d+),\s+cap=(\d+),\s+status=(passed_through|truncated)(?:,\s+kept_first=(\d+))?(?:,\s+kept_last=(\d+))?(?:,\s+dropped=(\d+))?(?:,\s+verdict=(SUCCESS|FAILURE))?(?:,\s+spill=([^\]\s]+))?\s*\]/);
    if (!m)
        return null;
    const total = parseInt(m[1], 10);
    const cap = parseInt(m[2], 10);
    const status = m[3];
    if (status === "passed_through") {
        return { total, cap, status };
    }
    // truncated — optional fields default to 0 / null
    const keptFirst = m[4] ? parseInt(m[4], 10) : 0;
    const keptLast = m[5] ? parseInt(m[5], 10) : 0;
    const dropped = m[6] ? parseInt(m[6], 10) : 0;
    const verdict = (m[7] === "FAILURE" ? "FAILURE" : "SUCCESS");
    const spillPath = m[8] && m[8] !== "null" ? m[8] : null;
    return { total, cap, status, keptFirst, keptLast, dropped, verdict, spillPath };
}
function BranchBadge({ git }) {
    if (!git || !git.is_git_repo || !git.branch)
        return null;
    return (_jsxs("div", { className: "pill min-w-0", title: git.last_commit_subject
            ? `${git.last_commit_sha} ${git.last_commit_subject}`
            : git.branch, children: [_jsx("span", { className: "text-subtle", children: "\u2325" }), _jsx("span", { className: "truncate font-mono text-text", children: git.branch }), git.dirty && _jsx("span", { className: "text-warn", title: "uncommitted changes", children: "\u25CF" })] }));
}
// ============================================================================
// NowChip — compact session-level "what's running right now" indicator pinned
// in the chat header. Replaces the old per-turn full-width banner; the user
// always sees the latest activity regardless of scroll position, and turns
// stay clean below.
// ============================================================================
function NowChip({ turn }) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, []);
    // Most recent running tool / agent — orchestrator OR any sub-agent's tools.
    const allTools = [
        ...turn.tools,
        ...Object.values(turn.agents).flatMap((a) => a.tools),
    ];
    const runningTool = [...allTools].reverse().find((t) => t.status === "running");
    const runningAgent = Object.values(turn.agents)
        .filter((a) => a.status === "running")
        .sort((a, b) => b.spawned_at - a.spawned_at)[0];
    let icon = "✦";
    let label = "Thinking";
    let startedAt = turn.startedAt;
    let toneText = "text-accent";
    let toneBorder = "border-accent/40 bg-accent/[0.06]";
    if (runningTool) {
        icon = "▸";
        label = runningTool.tool;
        startedAt = runningTool.startedAt;
        toneText = "text-warn";
        toneBorder = "border-warn/40 bg-warn/[0.05]";
    }
    else if (runningAgent) {
        icon = "▸";
        label = `Agent[${runningAgent.subagent_type}]`;
        startedAt = runningAgent.spawned_at;
        toneText = "text-accent-2";
        toneBorder = "border-accent-2/40 bg-accent-2/[0.05]";
    }
    else if (turn.assistantText) {
        icon = "✎";
        label = "Writing";
        toneText = "text-success";
        toneBorder = "border-success/40 bg-success/[0.05]";
    }
    return (_jsxs("div", { className: `inline-flex max-w-[220px] items-center gap-1.5 rounded-md border ${toneBorder} px-2 py-0.5 font-sans backdrop-blur-sm`, title: label, children: [_jsx("span", { className: "stream-dot shrink-0" }), _jsx("span", { className: `shrink-0 text-tx-xs ${toneText}`, children: icon }), _jsx("span", { className: "truncate text-tx-xs font-medium text-text", children: label }), _jsx("span", { className: "shrink-0 font-mono text-[10px] text-subtle", children: formatDuration(now - startedAt) })] }));
}
// ============================================================================
// HelpOverlay — modal listing the available slash commands + keyboard shortcuts.
// Same look as the ProjectList "new project" modal so the chrome is consistent.
// ============================================================================
const SHORTCUTS = [
    { keys: "↑ / ↓", desc: "Recall previous / next prompt from history" },
    { keys: "Esc", desc: "Cancel history navigation and restore your draft" },
    { keys: "Enter", desc: "Send the message" },
    { keys: "Double-click on a tool result", desc: "Expand / collapse the full preview" },
];
function HelpOverlay({ isAdmin, onClose }) {
    return (_jsx("div", { className: "fixed inset-0 z-30 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm", onClick: onClose, children: _jsxs("div", { onClick: (e) => e.stopPropagation(), className: "glass-card w-full max-w-lg space-y-5 p-6 animate-fade-in-up", children: [_jsxs("div", { className: "flex items-start justify-between gap-4", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-lg font-semibold tracking-tight", children: "Keyboard shortcuts & commands" }), _jsx("p", { className: "mt-0.5 text-xs text-muted", children: "Slash commands run locally \u2014 they don't cost a model call." })] }), _jsx("button", { onClick: onClose, className: "text-xl leading-none text-muted hover:text-text", title: "Close (Esc)", children: "\u00D7" })] }), _jsxs("div", { children: [_jsx("div", { className: "mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-subtle", children: "Slash commands" }), _jsx("div", { className: "divide-y divide-border/60 rounded-lg border border-border/60 bg-elevated/40", children: (isAdmin ? SLASH_COMMANDS : SLASH_COMMANDS.filter((c) => c.cmd !== "/debug")).map((c) => (_jsxs("div", { className: "flex items-baseline gap-3 px-3 py-2 text-sm", children: [_jsx("kbd", { className: "shrink-0 rounded border border-border bg-bg/60 px-1.5 py-0.5 font-mono text-tx-xs text-accent", children: c.cmd }), _jsx("span", { className: "text-muted", children: c.desc })] }, c.cmd))) })] }), _jsxs("div", { children: [_jsx("div", { className: "mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-subtle", children: "Keyboard" }), _jsx("div", { className: "divide-y divide-border/60 rounded-lg border border-border/60 bg-elevated/40", children: SHORTCUTS.map((s) => (_jsxs("div", { className: "flex items-baseline gap-3 px-3 py-2 text-sm", children: [_jsx("kbd", { className: "shrink-0 rounded border border-border bg-bg/60 px-1.5 py-0.5 font-mono text-tx-xs text-text", children: s.keys }), _jsx("span", { className: "text-muted", children: s.desc })] }, s.keys))) })] }), _jsx("div", { className: "text-right", children: _jsx("button", { onClick: onClose, className: "btn-ghost", children: "Close" }) })] }) }));
}
function EmptyState() {
    return (_jsxs("div", { className: "mt-16 flex flex-col items-center gap-4 text-center", children: [_jsx("div", { "aria-hidden": true, className: "relative flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-gradient shadow-glow-accent", children: _jsx("span", { className: "text-white text-2xl font-bold", children: "\u2318" }) }), _jsx("div", { className: "text-xl font-semibold tracking-tight text-text", children: "Start a conversation" }), _jsx("div", { className: "max-w-md text-sm text-muted", children: "Type a request below. Each prompt opens a turn that shows the agent's reply, every tool it calls, files it changes, and the tokens it used." })] }));
}
// ============================================================================
// DebugStream — floating panel showing the last N raw WebSocket events.
// Auto-scrolls; coloured by event kind so the human eye can spot bursts and
// gaps. The relative timestamp on each row is delta-from-previous so we can
// see whether events arrive smoothly or in a single end-of-turn batch.
// ============================================================================
const DEBUG_KIND_COLOR = {
    user_message: "text-text",
    assistant_text: "text-success",
    thinking_text: "text-accent-2",
    tool_start: "text-warn",
    tool_done: "text-success",
    agent_spawn: "text-accent-2",
    agent_status_update: "text-accent-2",
    file_changed: "text-accent",
    commit_made: "text-success",
    commit_skipped: "text-warn",
    push_done: "text-accent",
    token_update: "text-accent",
    todo_update: "text-muted",
    turn_summary: "text-text",
    error: "text-danger",
};
function DebugStream({ events, onClear, onClose, }) {
    const boxRef = useRef(null);
    // Drag state — desktop only. The header acts as the drag handle; click +
    // drag moves the panel via a transform offset from its CSS anchor
    // (sm:bottom-20 sm:right-4). Mobile uses the inline layout (under the
    // header) so dragging is disabled there.
    const [dragOff, setDragOff] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null);
    const onHeaderPointerDown = (e) => {
        // Only drag on desktop. matchMedia is the cheap way to gate.
        if (window.matchMedia("(max-width: 639px)").matches)
            return;
        // Don't start a drag if the click landed on a button (Clear / Close).
        if (e.target.closest("button"))
            return;
        dragRef.current = {
            startX: e.clientX, startY: e.clientY,
            baseX: dragOff.x, baseY: dragOff.y,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onHeaderPointerMove = (e) => {
        if (!dragRef.current)
            return;
        setDragOff({
            x: dragRef.current.baseX + (e.clientX - dragRef.current.startX),
            y: dragRef.current.baseY + (e.clientY - dragRef.current.startY),
        });
    };
    const onHeaderPointerUp = (e) => {
        if (!dragRef.current)
            return;
        dragRef.current = null;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch { }
    };
    // Follow-mode: stick to bottom while events arrive, unless the user has
    // scrolled up to inspect. Same UX as the main chat scroll.
    const [atBottom, setAtBottom] = useState(true);
    useEffect(() => {
        if (!atBottom)
            return;
        const el = boxRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
    }, [atBottom, events.length]);
    const onScroll = () => {
        const el = boxRef.current;
        if (!el)
            return;
        const threshold = 30;
        setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < threshold);
    };
    const jumpToBottom = () => {
        const el = boxRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
        setAtBottom(true);
    };
    const rows = events.map((e, i) => {
        const prev = i > 0 ? events[i - 1].ts : e.ts;
        const dt = e.ts - prev;
        const color = DEBUG_KIND_COLOR[e.kind] ?? "text-muted";
        let summary = "";
        if (typeof e.payload === "object" && e.payload) {
            const p = e.payload;
            if (e.kind === "tool_start")
                summary = `${p.tool}(${truncForDebug(p.target, 40)})`;
            else if (e.kind === "tool_done")
                summary = `${p.tool}${p.error ? " ✗" : " ✓"}`;
            else if (e.kind === "assistant_text")
                summary = `+${String(p.text ?? "").length} chars${p.done ? " · DONE" : ""}`;
            else if (e.kind === "thinking_text")
                summary = `+${String(p.text ?? "").length} chars`;
            else if (e.kind === "token_update")
                summary = `+${p.input_delta ?? 0} in / +${p.output_delta ?? 0} out`;
            else if (e.kind === "agent_spawn")
                summary = `${p.subagent_type} · ${truncForDebug(p.description, 40)}`;
            else if (e.kind === "agent_status_update")
                summary = `${p.agent_id?.slice?.(-6)} → ${p.status}`;
            else if (e.kind === "file_changed")
                summary = `${p.kind} ${p.path}`;
            else if (e.kind === "turn_summary")
                summary = `tools=${p.tools_used} in=${p.input_tokens} out=${p.output_tokens}`;
            else if (e.kind === "error")
                summary = String(p.message ?? "?");
            else
                summary = JSON.stringify(p).slice(0, 80);
        }
        return (_jsxs("div", { className: "flex items-baseline gap-2 whitespace-pre font-mono text-[11px] leading-tight", children: [_jsxs("span", { className: "w-12 shrink-0 text-right text-subtle", children: ["+", dt, "ms"] }), _jsx("span", { className: `w-32 shrink-0 ${color}`, children: e.kind }), _jsx("span", { className: "truncate text-muted", title: JSON.stringify(e.payload), children: summary })] }, i));
    });
    return (_jsxs("div", { style: 
        // Apply drag offset only when there IS one (and only on desktop, where
        // the panel is fixed-positioned). On mobile the panel is inline so
        // dragOff stays at {0,0} naturally and transform is a no-op.
        dragOff.x !== 0 || dragOff.y !== 0
            ? { transform: `translate(${dragOff.x}px, ${dragOff.y}px)` }
            : undefined, className: "\n        z-30 w-full overflow-hidden border-b border-border bg-bg/95\n        sm:fixed sm:bottom-20 sm:right-4 sm:w-[420px] sm:max-w-[calc(100vw-2rem)]\n        sm:rounded-lg sm:border sm:shadow-lift sm:backdrop-blur-md\n      ", children: [_jsxs("div", { onPointerDown: onHeaderPointerDown, onPointerMove: onHeaderPointerMove, onPointerUp: onHeaderPointerUp, onPointerCancel: onHeaderPointerUp, className: "flex select-none items-center justify-between border-b border-border bg-elevated/60 px-3 py-1.5 sm:cursor-move", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-[10px] font-bold uppercase tracking-[0.18em] text-accent", children: "Debug stream" }), _jsxs("span", { className: "font-mono text-[10px] text-subtle", children: [events.length, " events"] })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { type: "button", onClick: onClear, className: "text-[10px] text-muted hover:text-danger", children: "clear" }), _jsx("button", { type: "button", onClick: onClose, className: "flex h-5 w-5 items-center justify-center rounded text-muted hover:bg-elevated hover:text-text", title: "Close debug panel", "aria-label": "Close debug panel", children: "\u2715" })] })] }), _jsxs("div", { className: "relative", children: [_jsx("div", { ref: boxRef, onScroll: onScroll, className: "max-h-72 overflow-y-auto px-3 py-2", children: events.length === 0 ? (_jsx("div", { className: "font-sans text-[11px] text-subtle", children: "Waiting for events\u2026 send a message to see the stream." })) : (rows) }), !atBottom && events.length > 0 && (_jsx("button", { type: "button", onClick: jumpToBottom, className: "absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full border border-border bg-elevated/95 text-xs text-text shadow-lift backdrop-blur-md hover:border-accent/60 hover:bg-accent/15 hover:text-accent", title: "Jump to latest event", "aria-label": "Jump to latest event", children: "\u2193" }))] })] }));
}
// ============================================================================
// DeployStrip — sticky horizontal strip between the plan panel and the chat
// scroll area. Shows every app deployed FROM this session as a state-aware
// pill (slug + public URL + 🔄 Update when a fresh build is detected, ✓ Up
// to date when not). A single "+ Deploy new" button on the right opens the
// modal for a *new* app (first-time deploy or a sibling project in a
// multi-app session) — it is only enabled when the agent has produced a
// build since the last re-deploy of the default project, so the user never
// sees a "Deploy" button that would fail with "no dist/ found".
// ============================================================================
// "3m ago" / "2h ago" — short relative-time formatter for the
// per-pill "last deployed" badge. Lives at module scope so the
// pill doesn't re-allocate it on every render.
function timeAgoShort(epochSeconds) {
    if (!epochSeconds)
        return "";
    const sec = Math.max(1, Math.floor(Date.now() / 1000 - epochSeconds));
    if (sec < 60)
        return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60)
        return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24)
        return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
}
// ============================================================================
function DeployStrip({ sessionId, sessionName, apps, hasUnbuiltBuild, lastDetected, deployProgress, onUpdateClicked, onDeployed, onDeleted, onToggled, showModal: showModalProp, onShowModalChange, }) {
    const [localShow, setLocalShow] = useState(false);
    const showModal = showModalProp ?? localShow;
    const setShowModal = (v) => {
        if (onShowModalChange)
            onShowModalChange(v);
        else
            setLocalShow(v);
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "border-b border-border bg-elevated/40", children: _jsxs("div", { className: "mx-auto flex max-w-4xl flex-wrap items-center gap-2 px-4 py-2", children: [apps.length === 0 ? (_jsx("span", { className: "flex-1 text-tx-xs text-muted", children: hasUnbuiltBuild
                                ? "Build ready — click “Deploy new” to publish."
                                : "Build the app first (`npm run build`), then come back and click “Deploy new”." })) : (apps.map((a) => (_jsx(DeployedAppPill, { app: a, lastDetected: lastDetected, 
                            // Pass the live progress slice for this slug (if
                            // any). Pills fall back to local `busy` state when
                            // the progress event hasn't arrived yet.
                            progress: deployProgress[a.slug], onUpdateClicked: onUpdateClicked, onDeployed: onDeployed, onDeleted: onDeleted, onToggled: onToggled }, a.slug)))), _jsxs("button", { type: "button", onClick: () => setShowModal(true), disabled: !sessionId || !hasUnbuiltBuild, title: !hasUnbuiltBuild
                                ? apps.length === 0
                                    ? "Build the app first (`npm run build`) to enable"
                                    : "All built sub-apps are already deployed — use a pill's 🔄 Update instead"
                                : "Publish a new sub-app from this session", className: "ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-md border border-accent/40 bg-accent/15 px-3 py-1.5 text-tx-sm font-medium text-accent hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-40", children: [_jsx(UploadIcon, {}), " Deploy new"] })] }) }), showModal && (_jsx(DeployModal, { sessionId: sessionId, defaultName: sessionName, 
                // Pass the live list of already-deployed slugs so the
                // modal can do a CLIENT-SIDE collision check as the user
                // types — surfacing "this name is already taken" in the
                // slug field BEFORE the user clicks Deploy. Without this
                // the user only finds out the slug is taken when the
                // server returns 409, and the server (in some code
                // paths) used to silently overwrite the existing app —
                // clobbering their previous deploy with the new one. The
                // server's 409 path is still kept as a defense in depth.
                existingSlugs: new Set(apps.map((a) => a.slug)), onClose: () => setShowModal(false), onDeployed: (result) => {
                    onDeployed(result.app);
                    setShowModal(false);
                } }))] }));
}
function hostApps() {
    // Best-effort hint for the URL placeholder — we assume the current host
    // IS the apps root (most common setup). Real URL comes from the deploy
    // response which knows the server-side resolved root.
    if (typeof window === "undefined")
        return "your-domain";
    return window.location.host;
}
function DeployedAppPill({ app, lastDetected, progress, onUpdateClicked, onDeployed, onDeleted, onToggled, }) {
    // Server-computed URL is authoritative (handles apps-root domain
    // resolution + future routing). Fall back to a derived URL only
    // if the server didn't send one -- legacy rows pre-dating the
    // public_url field.
    const url = app.public_url
        || `${window.location.protocol}//${app.slug}.${window.location.host}/`;
    const [busy, setBusy] = useState(false);
    const state = app.state ?? "running";
    const isOff = state === "stopped" || state === "error";
    // Per-app "this build is newer than my last deploy" — server
    // stamps this per-candidate as `c.mtime > c.last_redeploy_at`,
    // so a rebuild of app B doesn't make app A's pill claim it's
    // stale. Pills without a matching candidate fall back to false
    // (e.g., when the user just deleted the dist).
    const appFreshBuild = lastDetected?.candidates.find((c) => c.deployed_slug === app.slug)?.is_fresh ?? false;
    // Reset busy when the deploy settles. The parent clears
    // `deployProgress[slug]` for this slug in BOTH the
    // `deploy_complete` and `deploy_failed` WS handlers, so flipping
    // from { phase, … } to undefined is the canonical "deploy is
    // settled, refresh the row" signal. Without this, the pill would
    // stay amber + "Deploying…" until the 60 s safety timeout fired,
    // which made a successful 12 s fullstack deploy look frozen.
    useEffect(() => {
        if (busy && !progress)
            setBusy(false);
    }, [busy, progress]);
    const remove = async () => {
        if (!confirm(`Take down ${app.slug}? The public URL will stop working.`))
            return;
        try {
            await deployedAppsApi.delete(app.slug);
            onDeleted(app.slug);
        }
        catch (e) {
            alert(`Delete failed: ${e?.message ?? "unknown"}`);
        }
    };
    const toggle = async () => {
        if (busy)
            return;
        setBusy(true);
        try {
            const next = isOff
                ? await deployedAppsApi.start(app.slug)
                : await deployedAppsApi.stop(app.slug);
            onToggled(app.slug, next.state);
        }
        catch (e) {
            alert(`Toggle failed: ${e?.message ?? "unknown"}`);
        }
        finally {
            setBusy(false);
        }
    };
    // Update = in-place re-deploy of the same slug. The server detects
    // slug+owner match and swaps the dist atomically (no new port, no
    // new systemd unit, no new URL). The deploy modal would also work
    // but is overkill for a re-deploy -- it asks for a slug, project,
    // and 12-step progress that the user has already seen once.
    //
    // We DON'T await the job here (it can take 10-30s for a fullstack).
    // Instead, the WS event handler in ChatPage listens for
    // deploy_complete / deploy_failed and refreshes deployedApps +
    // detectedDist. The local `busy` flips the pill to a "Deploying…"
    // chip so the user sees the click took effect; the WS event then
    // replaces the pill with the fresh row (which has the new
    // last_redeploy_at) and clears busy via the natural re-render.
    const update = async () => {
        if (busy)
            return;
        setBusy(true);
        // Tell the parent which slug was clicked, so the next
        // deploy_progress WS event (which doesn't carry a slug) can
        // be attributed to this pill.
        onUpdateClicked(app.slug);
        // Immediate user feedback: a toast on the side panel so the
        // user knows the click took effect even if they miss the
        // pill change. The deploy_complete WS event also fires a
        // success toast, which the user will see in sequence.
        try {
            // Use the global session toast (defined in lib/sessionContext).
            // The component may not have direct access to it; fall back
            // to a console log if the import isn't reachable from here.
            // The pill's amber pulse + the WS-driven success toast is
            // the primary UX, this is the belt-and-braces backup.
            // (Defer to the WS handler for the success toast — the in-flight
            // toast would be redundant and would race the success one.)
            await sessionApi.deploy(app.source_session_id ?? "", {
                slug: app.slug,
                project_dir: app.project_dir ?? undefined,
            });
            // Don't setBusy(false) here — leave the pill in the "Deploying…"
            // state until the deploy_complete / deploy_failed WS event
            // arrives and re-fetches the row. Fall back after 60s in case
            // the WS is wedged so the user isn't stuck on "Deploying…"
            // forever.
            setTimeout(() => setBusy(false), 60_000);
        }
        catch (e) {
            alert(`Update failed: ${e?.message ?? "unknown"}`);
            setBusy(false);
        }
    };
    // State dot colour mirrors the Settings page badge so the user
    // gets the same vocabulary across both surfaces.
    const dotClass = state === "running" ? "bg-emerald-500" :
        state === "starting" ? "bg-amber-500" :
            state === "error" ? "bg-rose-500" :
                "bg-zinc-500";
    const pillClass = state === "running" ? "border-success/30 bg-success/10 text-success" :
        state === "stopped" ? "border-border bg-elevated text-muted" :
            "border-border bg-elevated text-muted";
    // When busy, override the pillClass to a clearly different colour
    // (amber, with a pulse animation) so the user gets an instant
    // visual signal that the click registered. The default pillClass
    // is reused when not busy.
    const busyPillClass = "border-amber-400/50 bg-amber-400/10 text-amber-700 dark:text-amber-300 animate-pulse";
    return (_jsxs("span", { className: `inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-tx-xs ${busy ? busyPillClass : pillClass}`, "aria-busy": busy, "aria-live": busy ? "polite" : undefined, children: [_jsx("button", { type: "button", onClick: toggle, disabled: busy || state === "starting", "aria-label": isOff ? "Bring online" : "Pause", title: isOff ? "Paused — click to bring online" : "Running — click to pause", className: "inline-flex items-center", children: _jsx("span", { className: `inline-block size-2 rounded-full transition-opacity ${dotClass} ${busy ? "opacity-50" : ""}` }) }), _jsx("span", { className: "font-mono", title: url, children: app.slug }), _jsx("a", { href: url, target: "_blank", rel: "noreferrer", className: "hover:text-accent", title: url, children: "\u2197" }), app.last_redeploy_at > 0 && (_jsxs("span", { className: "text-tx-xs text-muted", title: new Date(app.last_redeploy_at * 1000).toISOString(), children: ["\u00B7 ", timeAgoShort(app.last_redeploy_at)] })), busy ? (
            // When the live progress event has arrived, show the
            // current step + step count so the user sees the
            // 10-30s fullstack deploy making real progress. Falls
            // back to a generic "Deploying…" pulse before the first
            // event lands (and after the 60s safety timeout).
            _jsxs("span", { className: "inline-flex items-center gap-1 rounded border border-amber-500/50 bg-amber-500/20 px-1.5 py-0.5 font-medium text-amber-700 dark:text-amber-300", title: progress
                    ? `Step ${progress.steps_done}/${progress.steps_total || "?"} — ${progress.phase}`
                    : "Deploy in progress… the deploy_complete WebSocket event will refresh this pill when it lands", children: [_jsx("span", { className: "inline-block size-1.5 animate-pulse rounded-full bg-amber-500" }), progress && progress.steps_total > 0
                        ? `${progress.phase} (${progress.steps_done}/${progress.steps_total})`
                        : "Deploying…"] })) : appFreshBuild ? (_jsx("button", { type: "button", onClick: update, title: `Push the new build to ${app.slug} (same URL, no new port)`, className: "inline-flex items-center gap-1 rounded border border-accent/40 bg-accent/15 px-1.5 py-0.5 font-medium text-accent hover:bg-accent/25", children: "\uD83D\uDD04 Update" })) : (_jsx("span", { className: "inline-flex items-center gap-1 rounded border border-success/30 bg-success/10 px-1.5 py-0.5 font-medium text-success", title: "Live and up to date \u2014 no new build since the last deploy", children: "\u2713 Up to date" })), _jsx("button", { type: "button", onClick: remove, className: "text-muted hover:text-danger", title: "Delete", "aria-label": "Delete app", children: "\u2715" })] }));
}
function DeployModal({ sessionId, defaultName, existingSlugs, onClose, onDeployed, }) {
    const [phase, setPhase] = useState("config");
    const [slug, setSlug] = useState(slugifyName(defaultName));
    const [detected, setDetected] = useState(null);
    const [detecting, setDetecting] = useState(true);
    const [projectDir, setProjectDir] = useState("");
    const [slugError, setSlugError] = useState(null);
    const [err, setErr] = useState(null);
    // Job-state refs held across the running-phase polling loop.
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [finalResult, setFinalResult] = useState(null);
    const pollAbortRef = useRef(null);
    // ---- Detect built dist on mount (same as the old modal).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const d = await sessionApi.detectedDist(sessionId);
                if (cancelled)
                    return;
                setDetected(d);
                // Pick the freshest UNBUILT candidate as the default
                // projectDir. The server's auto_pick is the freshest of
                // ALL candidates (including already-deployed ones), which
                // would point at a deployed row and fall outside the
                // unbuilt filter — leaving the picker showing nothing.
                // Fall back to d.auto_pick if no unbuilt candidate exists
                // (the picker handles that case via `noUnbuilt`).
                const unbuilt = d.candidates.filter((c) => !c.is_deployed);
                const pick = unbuilt[0]?.project_dir ?? d.auto_pick ?? "";
                if (pick)
                    setProjectDir(pick);
            }
            catch (e) {
                if (cancelled)
                    return;
                setErr(e?.message ?? "could not detect build");
            }
            finally {
                if (!cancelled)
                    setDetecting(false);
            }
        })();
        return () => { cancelled = true; };
    }, [sessionId]);
    // Filter candidates to UNBUILT sub-apps only. The modal is only
    // opened from "+ Deploy new" (which is disabled when every
    // sub-app is already deployed), so an already-deployed candidate
    // in the dropdown would 409 on submit. Drop them up front and
    // let the rest of the modal operate on the filtered list as if
    // it were the only set of candidates.
    const unbuiltCandidates = useMemo(() => (detected?.candidates ?? []).filter((c) => !c.is_deployed), [detected]);
    // Defensive guard: the strip disables "+ Deploy new" when
    // !has_unbuilt_build, so reaching this state from the UI is rare.
    // But if the modal is opened some other way and ALL detected
    // candidates are already deployed, show a clear message instead
    // of a dead empty picker.
    const noUnbuilt = !detecting
        && (detected?.candidates?.length ?? 0) > 0
        && unbuiltCandidates.length === 0;
    const multiple = unbuiltCandidates.length > 1;
    const noBuild = !detecting && (detected?.candidates?.length ?? 0) === 0;
    // ---- AbortController for the GET /deploy-jobs/{id} poll. We do
    // NOT abort the POST itself on unmount — the deploy should keep
    // running server-side so the user can come back and see the result.
    // Only the polling is aborted (so a dead-tab doesn't keep pinging
    // a finished job).
    useEffect(() => {
        return () => {
            if (pollAbortRef.current) {
                pollAbortRef.current.abort();
                pollAbortRef.current = null;
            }
        };
    }, []);
    // ---- Polling loop. Starts on phase="running", stops on phase
    // transition or unmount. 1.5s interval (matches the Admin page
    // cadence for similar live-data surfaces).
    useEffect(() => {
        if (phase !== "running" || !jobId)
            return;
        let cancelled = false;
        const tick = async () => {
            if (cancelled)
                return;
            pollAbortRef.current = new AbortController();
            try {
                const s = await sessionApi.deployJobStatus(sessionId, jobId, {
                    signal: pollAbortRef.current.signal,
                });
                if (cancelled)
                    return;
                setJobStatus(s);
                if (s.status === "succeeded") {
                    if (s.result)
                        setFinalResult(s.result);
                    setPhase("done");
                }
                else if (s.status === "failed" || s.status === "cancelled") {
                    setErr(s.error || (s.status === "cancelled" ? "Cancelled by you." : "Deploy failed."));
                    setPhase("failed");
                }
            }
            catch (e) {
                if (cancelled)
                    return;
                // AbortError on unmount is fine — ignore it. Any other network
                // error: keep polling (transient), don't flip the modal to failed.
                if (e?.name === "AbortError")
                    return;
                // 404 means the job is gone (server restart, very old job) — show failed.
                if (e?.status === 404) {
                    setErr("Deploy job not found on server. The backend may have restarted.");
                    setPhase("failed");
                }
            }
        };
        // Immediate first tick + 1.5s interval.
        void tick();
        const id = setInterval(tick, 1500);
        return () => {
            cancelled = true;
            clearInterval(id);
            if (pollAbortRef.current)
                pollAbortRef.current.abort();
        };
    }, [phase, jobId, sessionId]);
    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        setSlugError(null);
        // Defense-in-depth: don't even attempt the POST if the live
        // collision check has flagged the slug. The submit button is
        // already disabled when this is true, but a fast typist could
        // press Enter between renders — block it here too. The server
        // would 409 anyway, but not every code path on the server
        // used to reject the write (see the silent-overwrite bug this
        // PR is preventing), so client-side is the safer layer.
        if (slugTaken) {
            setSlugError(liveSlugError);
            return;
        }
        // Submit the deploy. The POST returns 202 + a job_id immediately.
        // We do NOT pass our own AbortSignal here — we want the deploy
        // to keep running server-side even if the user closes the modal
        // (they can come back and see the result via the deployed-apps
        // strip, which re-fetches from /api/sessions/{id}/deployed-apps).
        try {
            const start = await sessionApi.deploy(sessionId, {
                slug: slug || undefined,
                project_dir: projectDir || undefined,
            });
            setJobId(start.job_id);
            setJobStatus({
                job_id: start.job_id, session_id: sessionId, slug: start.slug,
                status: "pending", phase: "queued", steps: [], error: null,
                result: null, created_at: Date.now() / 1000, updated_at: Date.now() / 1000,
            });
            setPhase("running");
        }
        catch (e) {
            const msg = e?.message ?? "deploy failed";
            if (e?.status === 409 || /already taken|already deployed as/i.test(msg)) {
                // 409: slug collision OR sub-app already deployed under a
                // different slug. The server's error already names the
                // existing slug + URL, so just surface it as a field-level
                // message and stay in the config phase.
                setSlugError(msg.replace(/^409:\s*/, ""));
            }
            else {
                setErr(msg);
            }
        }
    };
    const cancel = async () => {
        if (!jobId)
            return;
        try {
            await sessionApi.cancelDeployJob(sessionId, jobId);
            // The polling tick will pick up the "cancelled" status on the
            // next iteration and flip to the failed view.
        }
        catch {
            // Best-effort. If the cancel request fails, the deploy will
            // still complete on its own and the poll will surface it.
        }
    };
    // `noBuild`, `multiple`, `noUnbuilt` are derived up top (right
    // after the detectedDist fetch) so the picker can filter to
    // unbuilt-only before computing these. `noUnbuilt` means "we
    // see builds, but they're all already deployed" — distinct from
    // `noBuild` ("we see no builds at all").
    //
    // Live slug-collision check. The user reported that typing a
    // slug that matches an existing app used to silently OVERWRITE
    // the existing deploy (clobbering their prior app). We now
    // surface the collision in the slug input itself as the user
    // types: red border, error text, Deploy button disabled. The
    // check uses the SAME `slugifyName` normalization the server
    // runs on submit, so a typed "My Cool App" is checked as
    // "my-cool-app" — the form's final shape, not the mid-type
    // string. Empty slug = no collision (just missing input, which
    // the `required` attr + submit button's `readyInConfig` gate
    // already handle). The server's 409 path is still kept as a
    // defense in depth — `existingSlugs` is a snapshot from when
    // the modal opened and could miss a deploy that landed in
    // another tab.
    const normalizedSlug = slugifyName(slug);
    const slugTaken = !!existingSlugs
        && normalizedSlug.length > 0
        && existingSlugs.has(normalizedSlug);
    // The user-facing message: name the existing slug, not just
    // "taken", so the user can recognise WHICH app it is and go
    // rename it / pick a different name. (We only have the slug,
    // not the app's display name, in the existing-slug set —
    // that's fine, the URL is what they'd care about.)
    const liveSlugError = slugTaken
        ? `“${normalizedSlug}” is already deployed. Pick a different slug, or take down the existing app from the Admin page.`
        : null;
    const readyInConfig = !detecting
        && !noBuild
        && !noUnbuilt
        && !!slug
        && !slugTaken; // collision blocks submit
    const isRunning = phase === "running";
    const isDone = phase === "done";
    const isFailed = phase === "failed";
    // Backdrop click: hard no-op while running. Same for ESC.
    const onBackdropClick = () => {
        if (!isRunning)
            onClose();
    };
    useEffect(() => {
        if (!isRunning)
            return;
        const onKey = (e) => {
            if (e.key === "Escape")
                e.stopPropagation();
        };
        // Capture phase so we intercept ESC before any other handler.
        window.addEventListener("keydown", onKey, { capture: true });
        return () => window.removeEventListener("keydown", onKey, { capture: true });
    }, [isRunning]);
    // ---- Body content for each phase.
    let body;
    if (isRunning || isDone || isFailed) {
        body = (_jsxs(_Fragment, { children: [_jsx("h3", { className: "font-serif text-xl font-semibold tracking-tight", children: isDone ? "Deployment complete" : isFailed ? "Deployment failed" : "Deploying…" }), isRunning && (_jsxs("p", { className: "text-tx-xs text-muted", children: [jobStatus?.phase || "Starting…", " \u2014 keep this window open until it finishes."] })), _jsx("ol", { className: "mt-1 space-y-1.5", "data-testid": "deploy-steps", children: Array.from({ length: 13 }).map((_, idx) => {
                        // Fallback labels — only used before the first poll.
                        // Must stay in lockstep with server/app.py _DEPLOY_STEPS.
                        const s = jobStatus?.steps?.[idx];
                        const label = s?.label ?? ["", "Validating build", "Reserving public URL",
                            "Copying build to /opt/ojas-apps", "Adding PWA defaults", "Copying backend",
                            "Creating virtualenv", "Installing Python dependencies",
                            "Writing systemd unit", "Enabling systemd service",
                            "Recording deployment", "Configuring reverse proxy",
                            "Pre-fetching TLS certificate", "Starting service + health check"][idx];
                        const status = s?.status ?? (isRunning ? "pending" : "pending");
                        const message = s?.message ?? null;
                        const isCurrent = status === "running";
                        const isDone_ = status === "done";
                        const isFailed_ = status === "failed";
                        return (_jsxs("li", { className: "flex items-start gap-2 text-tx-sm", children: [_jsx("span", { className: "mt-0.5 inline-flex size-4 shrink-0 items-center justify-center font-mono " +
                                        (isDone_ ? "text-success" :
                                            isFailed_ ? "text-danger" :
                                                isCurrent ? "text-accent" :
                                                    "text-subtle"), "aria-hidden": true, children: isDone_ ? "✓" :
                                        isFailed_ ? "✗" :
                                            isCurrent ? _jsx("span", { className: "inline-block size-3 animate-spin rounded-full border-2 border-accent border-t-transparent" }) :
                                                "○" }), _jsxs("div", { className: "flex-1", children: [_jsx("div", { className: isCurrent ? "font-medium text-text" :
                                                isDone_ ? "text-muted line-through opacity-70" :
                                                    isFailed_ ? "text-danger" :
                                                        "text-subtle", children: label }), message && (_jsx("div", { className: "mt-0.5 text-tx-xs " + (isFailed_ ? "text-danger" : "text-muted"), children: message }))] })] }, idx));
                    }) }), isDone && finalResult && (_jsxs("div", { className: "rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-tx-sm", children: [_jsx("div", { className: "text-success font-medium", children: "Live now" }), _jsx("a", { href: finalResult.url, target: "_blank", rel: "noreferrer", className: "mt-1 block break-all font-mono text-tx-xs text-accent hover:underline", children: finalResult.url })] })), isFailed && err && (_jsx("div", { className: "rounded border border-danger/30 bg-danger/10 px-3 py-2 text-tx-xs text-danger", children: err })), _jsxs("div", { className: "flex justify-end gap-2 pt-1", children: [isRunning && (_jsx("button", { type: "button", onClick: cancel, className: "btn-ghost", "data-testid": "deploy-cancel", children: "Stop deploy" })), isDone && (_jsxs(_Fragment, { children: [_jsx("a", { href: finalResult?.url ?? "#", target: "_blank", rel: "noreferrer", className: "btn-ghost", children: "Open in new tab" }), _jsx("button", { type: "button", onClick: () => {
                                        if (finalResult)
                                            onDeployed(finalResult);
                                        onClose();
                                    }, className: "btn-primary", children: "Done" })] })), isFailed && (_jsx("button", { type: "button", onClick: onClose, className: "btn-primary", children: "Dismiss" }))] })] }));
    }
    else {
        // Config phase — the original UI.
        body = (_jsxs(_Fragment, { children: [_jsx("h3", { className: "font-serif text-xl font-semibold tracking-tight", children: "Deploy this build" }), _jsxs("p", { className: "text-tx-xs text-muted", children: ["The build's ", _jsx("code", { className: "font-mono", children: "dist/" }), " will be copied to a permanent location and served at the URL below."] }), _jsxs("label", { className: "block", children: [_jsx("span", { className: "text-tx-xs font-medium text-muted", children: "Slug" }), _jsx("input", { type: "text", value: slug, 
                            // Don't auto-slugify on every keystroke -- the user might
                            // be typing "My Cool App" and we shouldn't mangle the
                            // input mid-type (cursor jumps, "hyphens disappear"
                            // because spaces are silently converted to dashes).
                            // We only normalize: lowercase + drop leading/trailing
                            // whitespace, so the user sees exactly what they typed
                            // + the URL preview updates live with their input.
                            // Full slug sanitization (replacing non-allowed chars,
                            // collapsing multiple hyphens) happens on submit via
                            // the server's _slugify, AND we show a hint below the
                            // input explaining what the final slug will look like.
                            onChange: (e) => {
                                const v = e.target.value.toLowerCase();
                                setSlug(v);
                                setSlugError(null);
                            }, onBlur: (e) => {
                                // On blur, normalize further: collapse whitespace +
                                // run the full slugify so the user sees the final form
                                // before they hit Deploy.
                                setSlug(slugifyName(e.target.value));
                            }, "aria-invalid": !!(slugError || liveSlugError), className: `field mt-1 font-mono ${(slugError || liveSlugError) ? "border-danger/60" : ""}`, placeholder: "weather-app", required: true, autoFocus: true }), _jsxs("span", { className: "mt-1 block text-tx-xs text-subtle", children: ["URL: ", _jsxs("span", { className: "font-mono", children: ["https://", slugifyName(slug) || "<slug>", ".", hostApps(), "/"] }), _jsxs("span", { className: "ml-2 text-muted", children: ["\u00B7 only ", _jsx("span", { className: "font-mono", children: "a-z 0-9 - _" }), " allowed, auto-normalized on submit"] })] }), (liveSlugError || slugError) && (_jsx("span", { className: "mt-1 block text-tx-xs text-danger", role: "alert", children: liveSlugError || slugError }))] }), _jsxs("div", { className: "block", children: [_jsxs("span", { className: "text-tx-xs font-medium text-muted", children: ["Project", " ", _jsxs("span", { className: "text-subtle", children: ["(", multiple ? "pick which app to publish" : "auto-detected from the agent's build", ")"] })] }), detecting ? (_jsxs("div", { className: "field mt-1 flex items-center gap-2 text-tx-xs text-muted", children: [_jsx("span", { className: "inline-block size-3 animate-spin rounded-full border-2 border-muted border-t-transparent" }), "Detecting build\u2026"] })) : noUnbuilt ? (_jsxs("div", { className: "field mt-1 flex items-center gap-2 font-mono text-tx-sm text-muted", children: [_jsx("span", { "aria-hidden": true, children: "\uD83D\uDD12" }), _jsx("span", { children: "All built sub-apps are already deployed \u2014 nothing new to publish." })] })) : multiple ? (_jsxs(_Fragment, { children: [_jsx("select", { value: projectDir, onChange: (e) => setProjectDir(e.target.value), className: "field mt-1 font-mono", "data-testid": "detected-subdir", children: unbuiltCandidates.map((c) => (_jsx("option", { value: c.project_dir, children: c.project_dir === "" ? "<session root>" : c.project_dir }, c.project_dir))) }), _jsx("p", { className: "mt-1 text-tx-xs text-muted", children: "Multiple unbuilt sub-apps \u2014 pick the one to publish." })] })) : (_jsxs("div", { className: "field mt-1 flex items-center gap-2 font-mono text-tx-sm", children: [_jsx("span", { "aria-hidden": true, className: "text-muted", children: "\uD83D\uDD12" }), _jsx("span", { "data-testid": "detected-subdir", className: "flex-1", children: projectDir === "" ? "<session root>" : (projectDir || "—") }), projectDir === "" && (_jsx("span", { className: "rounded bg-surface-2 px-1.5 py-0.5 text-tx-xs text-muted", children: "root" }))] }))] }), noBuild && (_jsxs("div", { className: "rounded border border-danger/30 bg-danger/10 px-3 py-2 text-tx-xs text-danger", children: ["No built ", _jsx("code", { className: "font-mono", children: "dist/" }), " found in this session. Ask the agent to run", " ", _jsx("code", { className: "font-mono", children: "npm run build" }), " and try again."] })), noUnbuilt && (_jsxs("div", { className: "rounded border border-warn/30 bg-warn/10 px-3 py-2 text-tx-xs text-warn", children: ["All built sub-apps are already deployed. To update an existing app, use its ", _jsx("span", { className: "font-mono", children: "\uD83D\uDD04 Update" }), " pill in the nav strip."] })), err && !noBuild && (_jsx("div", { className: "rounded border border-danger/30 bg-danger/10 px-3 py-2 text-tx-xs text-danger", children: err })), _jsxs("div", { className: "flex justify-end gap-2 pt-1", children: [_jsx("button", { type: "button", onClick: onClose, className: "btn-ghost", children: "Cancel" }), _jsx("button", { type: "submit", disabled: !readyInConfig, 
                            // Tooltip explains WHY the button is disabled — important
                            // when it's the slug collision (the only disabled-state
                            // that isn't obvious from the field above the button).
                            // The other disabled-states (detecting, no build) DO have
                            // visible UI in the picker area, so the title isn't
                            // needed for them — but a unified message is simpler
                            // and `noOpMessage` already names the most common case.
                            title: slugTaken ? "Slug is already taken" : (!readyInConfig ? "Pick a build to deploy" : undefined), className: "btn-primary", children: "Deploy" })] })] }));
    }
    return (_jsx("div", { role: "dialog", "aria-modal": true, onClick: onBackdropClick, className: "fixed inset-0 z-40 flex items-end justify-center bg-black/45 backdrop-blur-sm sm:items-center", children: _jsx("form", { onSubmit: isRunning ? (e) => e.preventDefault() : submit, onClick: (e) => e.stopPropagation(), className: "w-full max-w-md space-y-3 rounded-t-2xl border border-border bg-surface p-5 shadow-lift sm:rounded-2xl", children: body }) }));
}
function slugifyName(input) {
    return input
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        // DNS label max is 63 chars per RFC 1035. Caddy's wildcard
        // block rewrites `<slug>.<root>` and the root itself is
        // already ~20 chars, so 40 leaves ~3 chars of headroom on
        // the longest root domains. 60 is the safe ceiling; we
        // round to 60 to leave 3 chars for the root and the
        // trailing dot/slash.
        .slice(0, 60);
}
function UploadIcon() {
    return (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: _jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" }) }));
}
function truncForDebug(s, n) {
    const str = String(s ?? "");
    return str.length <= n ? str : str.slice(0, n - 1) + "…";
}
